/**
 * AI DP (Demand Planning) Scenario Planning
 * DP 모듈 전용 - 실제 DP 모듈 설정/데이터 정의 (소스 오브 트루스)
 * AiScenarioPlanning.js 가 이 파일에서 DP_MODULE 을 import 함
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "@plannel/utils/i18n";
import { format as formatDate } from "date-fns";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import dateUtils from "@plannel/utils/date-util";
import planVersionService from "@plannel/services/data/plan-version-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import authService from "@plannel/services/system/auth-service";
import DpVersionModal from "../demand-plan/dp-modal/DpVersionModal";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  IconButton, LinearProgress, Popover, Snackbar, Stack, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import scenarioService from "../../services/ai/scenarioService";
import customerHrchyService from "../../services/data/customer-hrchy-service";
// ⚠️ scenarioStatusUi 의 C(공용 팔레트)는 import 하지 않는다 — 이 파일은 로컬 C 를 쓴다(이름 충돌).
import { StatusSection, StatusKpiSection, KpiCardGrid, NavName, ForecastActualChart,
         StatusItemHeader, StatusItemRow, StatusItemList, StatusNotice, StatusEmpty,
         StatusEmptyPanel, StatusPendingTag, buildStatusPanelSummary,
         openPlanRunScreen, useLastRunSummary,
         StatusToolbar, StatusToolbarSelect, STATUS_TOOLBAR_BLOCK_H } from "./scenarioStatusUi";
import { FONT } from "@plannel/components/ai/typographyTokens";
import AiSegmentedControl from "./AiSegmentedControl";
import { withDpLayoutInitialDemand, paramRowSx, PARAM_ROW_PAD_X,
         PARAM_NUM_INPUT_W, PARAM_SELECT_W, isInlineEditable,
         PARAM_ROW_MIN_H, rememberModuleCatalog } from "./scenarioParamUtil";
import { fmtParamValue, currentPrefixFor, useEditEndGuard,
         ParamValueCells, ParamRevertButton } from "./scenarioParamCells";
import ScenarioParamAddPicker from "./ScenarioParamAddPicker";
import { FeasibilityHeroBadges, feasWarnCount } from "./CompareFeasibilityWarnings";

// ── DP 색상 팔레트 ─────────────────────────────────────────────────────────
const C = {
  primary: "#185FA5", primaryBg: "#E6F1FB", primaryL: "#B5D4F4",
  success: "#1D9E75", successBg: "#E1F5EE",
  warning: "#BA7517", warningBg: "#FAEEDA",
  danger:  "#E24B4A", dangerBg:  "#FCEBEB",
  neutral: "#6B7280", neutralBg: "#F3F4F6",
  purple:  "#7c3aed", purpleBg:  "#f5f3ff",
  teal:    "#0891b2", tealBg:    "#e0f2fe",
  border:  "#E5E7EB", bg:        "#F8F9FB",
};

// 달성률 수준 색상 — 값이 없으면(미집계) 판정 없음(중립). null >= 93 은 false 라
// 가드가 없으면 조용히 '심각(빨강)'으로 떨어진다.
function achieveColor(pct) {
  if (pct == null) return C.neutral;
  if (pct >= 93) return C.success;
  if (pct >= 85) return C.warning;
  return C.danger;
}

// 저달성/과조정 품목 flags 코드 상수 → 라벨 i18n 키 (백엔드가 한글 리터럴 대신 코드로 방출, breaking change)
const FLAG_LABEL_KEY = { LOW_ACHIEVE: "dpFlagLowAchieve", OVER_ADJUST: "dpFlagOverAdjust" };

// 현황 리스트 우측 칩 열 폭 — 실측 동기화(StatusItemList) 전 첫 페인트용 폴백값.
// 실제 폭은 전 행의 칩 실측 최대값으로 맞춰지므로 언어별로 재산정할 필요가 없다.
const GRP_CHIP_W  = 64; // 개선 필요 / 양호 (단일 칩)
// 저달성 · 과조정 — 행마다 유무가 갈리므로 종류별 슬롯을 고정한다(슬롯별 폭 실측 동기화).
const FLAG_ORDER  = ["LOW_ACHIEVE", "OVER_ADJUST"];
const FLAG_CHIP_W = [46, 46];
// 워크리스트 필터(세그먼트 컨트롤) 선택색 — 행 우측 플래그 칩과 같은 의미색을 쓴다
const FLAG_ACCENT = { LOW_ACHIEVE: C.warning, OVER_ADJUST: C.danger };

// 제품군 표 컬럼 폭 폴백 — 오차·추세·검토 사항을 이름 아래 인라인 문장에서 컬럼으로 분리
const GRP_MAPE_W    = 56;
const GRP_TREND_W   = 56;
const GRP_INSIGHT_W = 150;

// ── 현황 분석 탭: 수요추세 세그먼트 ────────────────────────────────────────────
// 카드로 세우는 3종은 순서를 고정한다. item_count 정렬이면 테넌트/새로고침마다 카드
// 위치가 튀어 클릭 근육기억이 깨진다 — 감소→성장→안정은 액션 우선순위 순서이기도 하다.
const SEG_ORDER     = ["DECLINING", "GROWING", "STABLE"];
// 라이프사이클 세그먼트 — 수요 추세가 아니라 품목 마스터로 정해지므로 카드가 아닌 칩 한 줄.
const SEG_LIFECYCLE = ["NPI", "EOS", "DISCONTINUED", "NO_HISTORY"];

function buildSegMeta(t) {
  return {
    GROWING:      { label: t("growthLabel"),            color: C.success, bg: C.successBg },
    STABLE:       { label: t("stableLabel"),            color: C.primary, bg: C.primaryBg },
    DECLINING:    { label: t("shrinking"),              color: C.danger,  bg: C.dangerBg  },
    NPI:          { label: t("npi"),                    color: C.teal,    bg: C.tealBg    },
    EOS:          { label: t("dpEosPlannedSegLabel"),   color: C.warning, bg: C.warningBg },
    // 판매종료일이 이미 지난 품목 — '단종예정(EOS)'과 구분 (백엔드 DISCONTINUED 세그먼트)
    DISCONTINUED: { label: t("dpDiscontinuedSegLabel"), color: C.neutral, bg: C.neutralBg },
    NO_HISTORY:   { label: t("dpNoHistorySegLabel"),    color: C.neutral, bg: C.neutralBg },
  };
}

// ── 주의 품목 워크리스트: 노출 행 수 ──────────────────────────────────────────
// 정렬 순서는 백엔드 랭킹(GREATEST(85-달성,0) + GREATEST(|조정|-30,0))을 그대로 쓴다.
//   프론트 정렬 컨트롤을 두지 않는 이유: 상위 5건만 보여주는 화면에서 정렬 기준을 바꾸는 건
//   심각도 상위가 아닌 다른 축을 파는 심층 분석이고, 그건 워크벤치의 몫이다.
// 노출 행 수 — 백엔드는 최대 50건을 내려주지만 항상 상위 N건만 보여준다.
//   현황 분석 탭은 "지금 어떤 상태인가"를 읽는 화면이지 문제 품목 전수 열람 화면이 아니다.
//   전수 조회는 워크벤치(품목 클릭 → 드릴다운)의 몫이라 펼침 토글을 두지 않는다.
const WORKLIST_TOP_N = 5;

// 워크리스트 컬럼 폭 폴백 — StatusItemList 실측 전 첫 페인트용
const WL_GROUP_W = 110;
const WL_SEG_W   = 72;
const WL_ADJ_W   = 66;

// 개선 필요 판정 — 제품군 카드 정렬과 상태 칩이 같은 식을 공유해야 한다
const needsImp = (g) => (g.avg_achievement || 0) < 88 || (g.over_plan_count || 0) > 0;

// 좌측 추이 차트 높이를 우측 정확도 스택 실측 높이에 맞춘다.
//   DP는 두 섹션 모두 CardContent 패딩이 "14px 15px" 로 같아 보정값이 0 이다(문서화용 상수).
//   MIN_H 아래로 내려가면 recharts 축·범례가 뭉개진다.
const DP_CHART_PAD_DIFF_PX = 0;
const DP_CHART_MIN_H       = 240;

// ── 현황 드릴다운 → DP 워크벤치(최근 릴리즈 버전 + 품목/제품군 필터) ──────────
// 워크벤치 코드는 건드리지 않고 viewState 주입만으로 처리한다(다른 화면 패턴과 동일).
//  1) selectVersion: DpVersionSelect 와 동일한 API(getAll)로 받은 "버전 객체 전체"를 주입.
//     → DpVersionSelect 가 동일 id 로 고른 객체와 deep-equal 이라 워크벤치의 버전전환 reset 이
//       일어나지 않고(필터 초기화·전체 재조회 방지) 주입한 advancedFilters 가 그대로 적용된다.
//  2) advancedFilters: 기간(baseDate, 계획구간) + 품목(itemCd) 또는 제품군(itemHrchyCd) 필터.
function buildDpDrillAdvancedFilter(version, cond) {
  const children = [];
  const cfg = version?.planConfigObj;
  if (cfg?.PLAN_START_DATE) {
    try {
      const start = formatDate(new Date(cfg.PLAN_START_DATE), "yyyy-MM-01");
      const endRaw = dateUtils.calculateRangeDate(
        new ZDate(cfg.PLAN_START_DATE), cfg.BUCKET_UNIT, cfg.START_DAY, cfg.PLANNING_RANGE, true);
      const end = formatDate(new Date(endRaw), "yyyy-MM-01");
      if (start && end) {
        children.push({ filterType: "date", headerName: "baseDate", filterName: "baseDate",
          operator: "inRange", filterUnit: "month", values: [start, end] });
      }
    } catch (e) { /* 기간 산출 실패 시 기간 필터 없이 진행 */ }
  }
  if (cond.itemCd) {
    children.push({ filterType: "string", headerName: "itemCd", filterName: "itemCd", operator: "equals", values: [String(cond.itemCd)] });
  } else if (cond.hrchyCd) {
    children.push({ filterType: "string", headerName: "itemHrchyCd", filterName: "itemHrchyCd", operator: "equals", values: [String(cond.hrchyCd)] });
  } else if (cond.hrchyName) {
    children.push({ filterType: "string", headerName: "itemHrchyNm", filterName: "itemHrchyNm", operator: "equals", values: [String(cond.hrchyName)] });
  }
  // baseDate 외 실제 필터(품목/제품군) 가 있어야 워크벤치가 "Advanced Filter 적용" 으로 인식한다.
  const hasTarget = children.some(c => c.headerName !== "baseDate");
  return hasTarget ? { logicOperator: "AND", children } : null;
}

export async function openDpWorkbench(dispatch, cond, version) {
  if (!version?.found || version?.id == null) return;   // 릴리즈 버전 없으면 무시
  const { itemCd, hrchyCd, hrchyName } = cond || {};
  if (!itemCd && !hrchyCd && !hrchyName) return;          // 필터 대상 없으면 무시
  const VIEW = "DP_WORKBENCH";

  // DpVersionSelect 와 동일한 호출로 버전 객체 전체를 받아 동일 객체를 주입(deep-equal 보장)
  let fullVersion = null;
  try {
    const res = await planVersionService.getAll({
      moduleCd: "DP", statusCds: ["RELEASED", "SCENARIO", "REFERENCE"],
      orderByColumn: "createdTs", sortType: "desc",
    });
    const list = res?.data?.results ?? [];
    fullVersion = list.find(v => String(v.id) === String(version.id)) ?? null;
  } catch (e) { /* 조회 실패 시 아래에서 폴백 */ }
  if (!fullVersion) {
    // 폴백: 최소 정보만으로 이동(필터는 미적용될 수 있음)
    fullVersion = { id: version.id, name: String(version.name ?? "") };
  }

  const advancedFilters = buildDpDrillAdvancedFilter(fullVersion, { itemCd, hrchyCd, hrchyName });
  const payload = { selectVersion: fullVersion };
  if (advancedFilters) payload.advancedFilters = advancedFilters;

  dispatch(reduxUtil.removeViewState({ viewName: VIEW }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(VIEW, payload)));
  dispatch(reduxUtil.updateHistoryState(VIEW));
}

// ── 수요추세 세그먼트: 도넛 + 요약 3행 ────────────────────────────────────────
// 세그먼트 카드 3장(각 장에 6M 스파크라인)에서 도넛 1개 + 요약 3행으로 교체했다.
//   ① 세그먼트는 "어디에 품목이 몰려 있나"가 본질이라 면적으로 읽히는 편이 정확하고,
//   ② 폭 120px·축 없는 스파크라인은 사실상 읽히지 않아 그 자리를 저달성·과조정에 내줬다.
// ⚠️ 아래 치수는 확정 디자인 스펙이다. 반응형으로 늘리거나(%/vw/flex:1) 카드 폭에 맞춰
//   키우지 말 것 — 도넛 236, 요약 150 은 고정값이다.
// 도넛 렌더 크기 = 좌측 블록 높이를 정하는 값. 요약 블록은 align-items:stretch 로 이 높이에
//   맞춰 늘어나므로(셀 = 높이/3), 이 값 하나만 올리면 도넛·요약이 함께 커진다.
//   ⚠️ 너무 키우면 우측 워크리스트(≈220)보다 높아져 섹션 전체가 따라 늘어난다.
const DONUT_PX      = 192;
const DONUT_VB      = 200;   // SVG viewBox 좌표계 — 반지름·선두께는 이 좌표계 기준
const DONUT_SW      = 28;
// 반지름은 링 "중심선" 기준이라 stroke 가 좌우로 SW/2 씩 삐져나온다.
//   r=70 이면 바깥 지름이 168/200 = 84% 라 래퍼에 16% 가 빈 여백으로 남는다.
//   VB/2 - SW/2 로 잡아 링 바깥이 뷰박스에 딱 맞게 한다(래퍼 크기 = 보이는 도넛 크기).
const DONUT_R       = DONUT_VB / 2 - DONUT_SW / 2;
const DONUT_GAP     = 3;     // 조각 사이 간격(viewBox 길이 단위)
const DONUT_TRACK   = "#f1f5f9";
const SUMMARY_W     = 168;   // 요약 블록 고정 폭
const SEG_LINE      = "#eef2f7";   // 요약 그리드 구분선·테두리
const SEG_TEXT      = "#1e293b";   // 숫자·세그먼트명
const SEG_TEXT_MUTE = "#94a3b8";   // "총 품목", 저달성·과조정
const SEG_TEXT_SUB  = "#64748b";   // "N개 세그먼트"
// 도넛/요약 전용 색 — 시안 확정값. buildSegMeta 의 팔레트 C 색(칩·표와 공유)과 별개다.
const SEG_VIZ = {
  DECLINING: { color: "#dc2626", tint: "#fef2f2" },
  GROWING:   { color: "#059669", tint: "#f0fdf4" },
  STABLE:    { color: "#2563eb", tint: "#eff6ff" },
};
const DONUT_CIRC = 2 * Math.PI * DONUT_R;

// data = 조각으로 그릴 세그먼트(0 건 제외) / all = 중앙 텍스트 조회용 전체(0 건 포함).
//   둘을 나누는 이유: 0 건 세그먼트도 요약 행에는 남아 hover 되므로 중앙 텍스트는 찾을 수 있어야 한다.
// recharts 대신 SVG circle 4개로 그린다 — 고정 치수·고정 간격(dasharray)이 스펙이라
//   ResponsiveContainer/Sector 의 자동 배치가 오히려 방해된다.
function DpSegmentDonut({ data, all, segMeta, total, segCount, activeSeg, onHover, onToggle, t }) {
  const act     = activeSeg ? all.find(d => d.seg === activeSeg) : null;
  const actMeta = act ? segMeta[act.seg] : null;
  const actViz  = act ? SEG_VIZ[act.seg] : null;
  const pct = act && total > 0 ? Math.round((act.value / total) * 1000) / 10 : null;
  // 각 조각의 호 길이와 시작 위치(누적) — dashoffset 은 음수로 줘야 시계방향으로 밀린다
  let acc = 0;
  const arcs = data.map((d) => {
    const len = total > 0 ? (d.value / total) * DONUT_CIRC : 0;
    const arc = { seg: d.seg, len, offset: acc };
    acc += len;
    return arc;
  });
  return (
    <Box sx={{ position: "relative", width: DONUT_PX, height: DONUT_PX,
               flex: "none", alignSelf: "center" }}>
      <svg viewBox={`0 0 ${DONUT_VB} ${DONUT_VB}`} width={DONUT_PX} height={DONUT_PX}
        style={{ display: "block", transform: "rotate(-90deg)" }}>
        <circle cx={DONUT_VB / 2} cy={DONUT_VB / 2} r={DONUT_R} fill="none"
          stroke={DONUT_TRACK} strokeWidth={DONUT_SW} />
        {arcs.map((a) => {
          // 조각 사이 간격은 각 호를 GAP 만큼 짧게 그려서 만든다(전체 100% 한 조각이면 간격 없음)
          const dash = Math.max(0, a.len - (arcs.length > 1 ? DONUT_GAP : 0));
          return (
            <circle key={a.seg}
              cx={DONUT_VB / 2} cy={DONUT_VB / 2} r={DONUT_R} fill="none"
              stroke={SEG_VIZ[a.seg].color} strokeWidth={DONUT_SW}
              strokeDasharray={`${dash} ${DONUT_CIRC - dash}`}
              strokeDashoffset={-a.offset}
              opacity={activeSeg && a.seg !== activeSeg ? 0.22 : 1}
              onMouseEnter={() => onHover(a.seg)}
              onMouseLeave={() => onHover(null)}
              onClick={() => onToggle(a.seg)}
              style={{ cursor: "pointer", transition: "opacity .18s" }} />
          );
        })}
      </svg>
      {/* 중앙 텍스트 — 조각/셀 hover(또는 선택) 시 해당 세그먼트로 바뀐다.
          pointerEvents:none 이 없으면 중앙 텍스트가 조각의 mouseleave 를 유발해 깜빡인다. */}
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                 alignItems: "center", justifyContent: "center", gap: "2px",
                 pointerEvents: "none", px: 1, textAlign: "center" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.3,
                          color: act ? actViz.color : SEG_TEXT_MUTE,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          maxWidth: "100%" }}>
          {act ? actMeta.label : t("dpSegTotalItems")}
        </Typography>
        <Typography sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1.1,
                          color: act ? actViz.color : SEG_TEXT,
                          fontVariantNumeric: "tabular-nums" }}>
          {(act ? act.value : total).toLocaleString()}
        </Typography>
        <Typography sx={{ fontSize: 11, color: SEG_TEXT_SUB,
                          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {act ? `${pct}%` : t("dpSegCountSuffix", { count: segCount })}
        </Typography>
      </Box>
    </Box>
  );
}

function DpSegmentSummaryCell({ seg, meta, info, selected, hovered, onHover, onToggle, t }) {
  const viz    = SEG_VIZ[seg];
  const growth = info?.avg_growth_rate_pct;
  const low    = info?.low_achieve_count;
  const over   = info?.over_adjust_count;
  // 구버전 응답이면 해당 조각을 통째로 생략한다(기존 카드의 != null 판정과 동일 규칙)
  const flagText = [
    low  != null ? t("dpSegLowAchieveChip",  { count: low })  : null,
    over != null ? t("dpSegOverAdjustChip",  { count: over }) : null,
  ].filter(Boolean).join(" · ");
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onToggle(seg)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(seg); }
      }}
      onMouseEnter={() => onHover(seg)}
      onMouseLeave={() => onHover(null)}
      sx={{
        // hover 와 선택(클릭 필터)이 같은 tint 를 쓰되, 선택은 좌측 컬러 바로 고정 표시한다
        bgcolor: (hovered || selected) ? viz.tint : "#fff",
        boxShadow: selected ? `inset 2px 0 0 ${viz.color}` : "none",
        transition: "background-color .18s",
        p: "6px 10px", minWidth: 0, minHeight: 0, cursor: "pointer", overflow: "hidden",
        // 4줄 세로 스택이면 셀이 73px 이 되어 좌측 블록이 우측 워크리스트보다 훨씬 높아진다.
        //   같은 정보를 2줄로 접어(이름↔증감률 / 품목수↔저달성·과조정) 높이를 ~56px 로 낮췄다.
        display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px",
      }}
    >
      {/* 1줄: 컬러 점 + 세그먼트명 ······ 증감률 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: viz.color,
                   flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: SEG_TEXT, lineHeight: 1.2,
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>
          {meta.label}
        </Typography>
        {growth != null && (
          <Typography sx={{ ml: "auto", pl: 0.5, fontSize: 12, fontWeight: 700, lineHeight: 1.2,
                            color: viz.color, fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap", flexShrink: 0 }}>
            {growth > 0 ? "+" : ""}{growth}%
          </Typography>
        )}
      </Box>
      {/* 2줄: 품목수 ······ 저달성 n · 과조정 n */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: SEG_TEXT,
                          fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
          {(info?.item_count ?? 0).toLocaleString()}
        </Typography>
        {flagText && (
          <Typography sx={{ ml: "auto", fontSize: 12, color: SEG_TEXT_MUTE, lineHeight: 1.3,
                            fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                            overflow: "hidden", textOverflow: "ellipsis" }}>
            {flagText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── 통합 섹션 안의 소제목 (IP 「문제 항목 TOP 5」의 부족/과잉 소제목과 동일 규격) ──────
//   카드 헤더가 아니라 본문 안이므로 접힘 토글과 무관하다(툴팁 아이콘 클릭이 새지 않는다).
function DpBlockHeading({ title, sub, tip, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: "text.primary",
                        whiteSpace: "nowrap" }}>
        {title}
      </Typography>
      {tip && (
        <Tooltip title={tip} placement="top" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: C.neutral, cursor: "help" }} />
        </Tooltip>
      )}
      {sub && (
        <Typography sx={{ fontSize: FONT.BODY, color: C.neutral, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sub}
        </Typography>
      )}
      {/* 우측 슬롯 — 워크리스트는 필터 칩까지 여기에 얹어 한 줄로 접는다(칩 전용 줄 제거).
          minWidth:0 이 없으면 칩 묶음이 제목을 밀어내 제목이 잘린다. */}
      {right && (
        <Box sx={{ ml: "auto", minWidth: 0, display: "flex", alignItems: "center",
                   flexWrap: "wrap", justifyContent: "flex-end", gap: 0.6 }}>
          {right}
        </Box>
      )}
    </Box>
  );
}

function DpSegmentBlock({ segment, segMeta, selectedSegment, onToggle }) {
  const { t } = useTranslation();
  const [hoverSeg, setHoverSeg] = useState(null);
  const has  = (s) => segment[s] != null;
  const segs = SEG_ORDER.filter(has);
  // 합계·비율은 도넛에 올라간 3종만으로 낸다 — 라이프사이클(칩 줄)은 수요 추세가 아니라
  //   품목 마스터로 갈린 별개 축이라 같은 분모에 넣으면 비율이 의미를 잃는다.
  const total = segs.reduce((acc, s) => acc + Number(segment[s]?.item_count ?? 0), 0);
  // 0 건 세그먼트는 조각에서 뺀다(호 길이 0 인데 GAP 만큼 음수가 되면 dasharray 가 깨진다).
  //   요약 셀에는 남긴다 — 0 건도 정보다.
  const segData = segs.map(s => ({ seg: s, value: Number(segment[s]?.item_count ?? 0) }));
  const pieData = segData.filter(d => d.value > 0);
  // hover 가 있으면 hover 우선, 없으면 선택된 세그먼트를 강조 상태로 유지한다.
  //   selectedSegment 는 라이프사이클 코드일 수도 있어 도넛 3종일 때만 강조로 인정한다.
  const activeSeg = (hoverSeg && segs.includes(hoverSeg)) ? hoverSeg
    : (selectedSegment && segs.includes(selectedSegment)) ? selectedSegment : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DpBlockHeading title={t("dpDemandSegmentTitle")} sub={t("dpRecent3mVs3mSubtitle")}
        tip={t("dpSegDefTooltip")} />
      {/* 카드 body — 치수는 고정값이다(도넛 168 / 요약 168 / gap 32 / p 6·20).
          추세 3종이 하나도 없으면(라이프사이클만 내려온 테넌트) 빈 링·빈 표 대신 통째로 생략한다.
          my:auto — 좌측 컬럼은 grid stretch 로 우측 워크리스트 높이만큼 늘어난다. body 가 자연
          높이뿐이라 남는 세로 공간이 전부 아래로 몰려 위에 붙어 보인다. 위아래 auto 마진으로
          남는 공간을 균등 분배해 제목/칩 사이 중앙에 놓는다. */}
      {segs.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "stretch", justifyContent: "center",
                   gap: "32px", p: "6px 20px", my: "auto" }}>
          <DpSegmentDonut
            data={pieData} all={segData} segMeta={segMeta}
            total={total} segCount={segs.length} activeSeg={activeSeg}
            onHover={setHoverSeg} onToggle={onToggle} t={t} />
          <Box sx={{ width: `${SUMMARY_W}px`, flex: "none",
                     display: "grid", gridTemplateRows: `repeat(${segs.length}, 1fr)`,
                     gap: "1px", bgcolor: SEG_LINE, border: `1px solid ${SEG_LINE}`,
                     borderRadius: "5px", overflow: "hidden" }}>
            {segs.map((s) => (
              <DpSegmentSummaryCell key={s} seg={s} meta={segMeta[s]} info={segment[s]} t={t}
                selected={selectedSegment === s} hovered={hoverSeg === s}
                onHover={setHoverSeg} onToggle={onToggle} />
            ))}
          </Box>
        </Box>
      )}
      {/* 라이프사이클 세그먼트 — 수요 추세 축이 아니라 도넛에 넣지 않고 칩 한 줄로 접는다 */}
      {SEG_LIFECYCLE.some(has) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 1 }}>
          {SEG_LIFECYCLE.filter(has).map((s) => {
            const on = selectedSegment === s;
            const m  = segMeta[s];
            return (
              <Chip key={s} size="small" clickable onClick={() => onToggle(s)}
                label={`${m.label} ${segment[s].item_count ?? 0}`}
                sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      color:   on ? m.color : C.neutral,
                      bgcolor: on ? m.bg    : C.neutralBg,
                      border: `1px solid ${on ? m.color : "transparent"}` }} />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ── 주의 품목 워크리스트 (통합 섹션 우측 블록) ────────────────────────────────
// 필터 두 축: 세그먼트(어떤 수요 추세인가) ∧ 플래그(무엇이 잘못됐나). 두 축은 AND.
//   각 축 내부는 단일선택(라디오) — 활성 세그먼트를 다시 누르면 해제된다(allowEmpty).
//   축마다 세그먼트 컨트롤(회색 트랙)을 하나씩 두는 이유: 칩 5개를 한 줄에 늘어놓으면 어디까지가
//   한 축인지 형태로 드러나지 않아, 다중선택인지 배타인지를 눌러 보기 전에는 알 수 없다.
//   트랙이 곧 축의 경계이고 「이 안에서 하나」라는 배타성을 그림으로 말해 준다.
//   플래그를 다중선택(OR)으로 두면, 모든 주의 품목이 최소 한 플래그를 가지므로
//   「둘 다 선택」의 결과가 「아무것도 선택 안 함」과 완전히 같아져 상태 하나가 낭비된다.
//   「감소 세그먼트」 칩은 별도 상태가 아니라 selectedSegment==="DECLINING" 의 별칭이다.
//   (독립 상태로 두면 카드=성장 + 칩=감소 라는 공집합 상태가 만들어져 표가 이유 없이 빈다)
function DpWorklistBlock({
  items, segment, segMeta, selectedSegment, onToggleSegment,
  flagFilter, onToggleFlag,
  navOk, onNavItem, sectionRef,
}) {
  const { t } = useTranslation();
  // 필터만 적용 — 순서는 서버 심각도 랭킹을 그대로 보존한다
  const filtered = useMemo(() => (items || []).filter((it) => {
    if (selectedSegment && it.segment !== selectedSegment) return false;
    if (flagFilter && !(it.flags || []).includes(flagFilter)) return false;
    return true;
  }), [items, selectedSegment, flagFilter]);

  const shown = filtered.slice(0, WORKLIST_TOP_N);
  // 세그먼트 옵션: 카드로 세우는 3종은 항상 노출(하나만 있으면 나머지가 왜 없는지 읽히지 않는다).
  //   라이프사이클 세그먼트는 카드가 아닌 세그먼트 섹션 칩에서 고르므로, 선택됐을 때만 되비춘다.
  const segOptions = useMemo(() => ([
    ...SEG_ORDER.filter((s) => segment?.[s] != null),
    ...(selectedSegment && !SEG_ORDER.includes(selectedSegment) ? [selectedSegment] : []),
  ].map((s) => ({ value: s, label: segMeta[s]?.label ?? s }))), [segment, selectedSegment, segMeta]);

  const flagOptions = useMemo(
    () => FLAG_ORDER.map((fl) => ({ value: fl, label: t(FLAG_LABEL_KEY[fl] || fl) })), [t]);

  return (
    <Box ref={sectionRef} sx={{ display: "flex", flexDirection: "column" }}>
      {/* 필터를 제목 줄로 올려 필터 전용 줄(약 30px)을 없앤다.
          건수 칩·초기화 칩은 두지 않는다 — 표시 건수는 필터에 따라 5개 미만으로 흔들려
          「상위 5 / N개」와 「N개」를 오가고, 초기화 칩은 필터가 없을 때 자리만 차지한다.
          해제는 활성 세그먼트를 다시 누르는 것으로 충분하다(각 축이 라디오라 상태가 명확하다).
          선택색(accent)은 선택된 값의 의미색을 그룹에 넘긴다 — 배타 선택이라 동시에 켜지는
          세그먼트가 하나뿐이므로, 옵션별로 색을 심는 것과 결과가 같으면서 배선이 단순하다.
          flexWrap:nowrap — 트랙 하나가 두 줄로 쪼개지면 알약 형태가 깨진다. 줄바꿈은 헤더
          우측 슬롯(flexWrap:wrap)에서 그룹 통째로 일어나게 두고, flexShrink:0 으로 압착도 막는다. */}
      <DpBlockHeading title={t("dpAttentionWorklistTitle")} sub={t("dpAttentionWorklistSubtitle")}
        right={(
          <>
            <AiSegmentedControl allowEmpty aria-label={t("filter")}
              value={flagFilter} onChange={onToggleFlag} options={flagOptions}
              accent={FLAG_ACCENT[flagFilter]}
              sx={{ flexWrap: "nowrap", flexShrink: 0 }} />
            {/* 옵션이 하나도 없으면 빈 회색 알약만 남으므로 그룹 자체를 생략한다 */}
            {segOptions.length > 0 && (
              <AiSegmentedControl allowEmpty aria-label={t("dpSegmentColLabel")}
                value={selectedSegment} onChange={onToggleSegment} options={segOptions}
                accent={segMeta[selectedSegment]?.color}
                sx={{ flexWrap: "nowrap", flexShrink: 0, ml: 0.4 }} />
            )}
          </>
        )} />

      {/* 표는 필터 결과가 몇 건이든 항상 렌더한다 — 헤더까지 사라지면 블록 높이가 더 크게 튄다.
          0건일 때도 열 폭 폴백(w/rightW)이 헤더를 잡아주므로 레이아웃이 무너지지 않는다. */}
      <StatusItemList>
        <StatusItemHeader
          nameLabel={`${t("item")} / ${t("itemCd")}`}
          rightW={FLAG_CHIP_W}
          barValue={{ w: 100, label: t("achievementRateLabel"), unit: "%" }}
          cells={[
            { w: WL_GROUP_W, label: t("itemGroupLabel"), align: "left" },
            { w: WL_SEG_W,   label: t("dpSegmentColLabel"), align: "center" },
            { w: WL_ADJ_W,   label: t("dpAdjustmentLabel"), unit: "%" },
          ]} />
        {/* 필터로 행이 줄어도 섹션이 흔들리지 않는 처리는 여기(행 영역)가 아니라
            부모 grid 의 실측 바닥(minHeight)이 담당한다 — 헤더·칩·패딩까지 전부 포함해야
            높이가 완전히 고정되는데, 행 높이 산식만으로는 그 차액을 못 잡는다. */}
        <Stack spacing={0.7}>
          {filtered.length === 0 && (
            <StatusNotice>{t("itemDiffFilterNoMatch")}</StatusNotice>
          )}
          {shown.map((it, idx) => {
            const ac  = achieveColor(it.achievement_pct || 0);
            const adj = it.adjustment_pct;
            const sm  = it.segment ? segMeta[it.segment] : null;
            const itemNav = navOk && it.item_cd;
            return (
              <StatusItemRow key={it.item_cd ?? idx}
                accent={ac}
                name={<NavName targets={[
                  { label: i18n.t("menuDpPractitionerWorkbench"),
                    onClick: itemNav ? () => onNavItem(it) : undefined },
                ]}>{it.item_name}</NavName>}
                sub={it.item_cd ?? ""}
                // 달성률 게이지 — 값이 없으면 null(스페이서). false 는 바 열 자체를 없애 헤더와 어긋난다.
                bar={it.achievement_pct != null
                  ? { value: Math.min(Math.max(it.achievement_pct, 0), 100), color: ac }
                  : null}
                barValue={{ w: 100, unit: it.achievement_pct != null ? "%" : "", num: (
                  <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: ac, lineHeight: 1,
                                    fontVariantNumeric: "tabular-nums" }}>
                    {it.achievement_pct != null ? it.achievement_pct : "—"}
                  </Typography>
                ) }}
                cells={[
                  { w: WL_GROUP_W, align: "left", node: (
                    <Typography sx={{ fontSize: FONT.BODY, color: C.neutral, lineHeight: 1,
                                      whiteSpace: "nowrap" }}>
                      {it.item_group || "—"}
                    </Typography>
                  ) },
                  { w: WL_SEG_W, align: "center", node: sm ? (
                    <Chip size="small" label={sm.label}
                      sx={{ height: 19, fontSize: FONT.CAPTION, fontWeight: 700, flexShrink: 0,
                            bgcolor: sm.bg, color: sm.color,
                            "& .MuiChip-label": { px: "6px" } }} />
                  ) : (
                    <Typography sx={{ fontSize: FONT.BODY, color: C.neutral, lineHeight: 1 }}>—</Typography>
                  ) },
                  // 조정률(SF 대비 확정계획 변동) — 값은 항상 보여주고 주의 수준(|adj|>30)만 강조.
                  //   저달성으로 걸린 품목도 담당자가 얼마나 손댔는지가 진단에 쓰인다.
                  { w: WL_ADJ_W, unit: adj != null ? "%" : "", num: (
                    <Typography sx={{ fontSize: FONT.SUB, lineHeight: 1,
                                      fontVariantNumeric: "tabular-nums",
                                      fontWeight: adj != null && Math.abs(adj) > 30 ? 800 : 400,
                                      color: adj != null && Math.abs(adj) > 30 ? C.danger : C.neutral }}>
                      {adj != null ? `${adj > 0 ? "+" : ""}${adj}` : "—"}
                    </Typography>
                  ) },
                ]}
                rightW={FLAG_CHIP_W}
                // 종류별 고정 슬롯 — 플래그가 하나뿐인 행에서도 나머지 칩이 제자리를 지킨다
                right={FLAG_ORDER.map((fl) => ((it.flags || []).includes(fl) ? (
                  <Chip label={t(FLAG_LABEL_KEY[fl] || fl)} size="small"
                    sx={{ height: 19, fontSize: FONT.CAPTION, fontWeight: 700, flexShrink: 0,
                          bgcolor: fl === "LOW_ACHIEVE" ? C.warningBg : C.dangerBg,
                          color:   fl === "LOW_ACHIEVE" ? C.warning   : C.danger }} />
                ) : null))} />
            );
          })}
        </Stack>
      </StatusItemList>
    </Box>
  );
}

// ── DP 현황 조회 전용 컴포넌트 ─────────────────────────────────────────────
// AiScenarioPlanning.js 의 현황 조회(Step 0)에서 mod.ExtraInitContent 로 렌더됨
export function DpExtraInitContent({ mod, phase, versionInfo, cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const goToWorkbenchByItem = useCallback(
    (it) => openDpWorkbench(reduxDispatch, { itemCd: it?.item_cd, itemName: it?.item_name }, versionInfo),
    [reduxDispatch, versionInfo],
  );
  const goToWorkbenchByGroup = useCallback(
    (grp) => openDpWorkbench(reduxDispatch, { hrchyCd: grp?.item_group_cd, hrchyName: grp?.item_group }, versionInfo),
    [reduxDispatch, versionInfo],
  );
  // 릴리즈 버전이 있어야 워크벤치 이동 가능 + 그 화면이 이 테넌트/사용자 권한 안이어야 한다.
  const navOk = versionInfo?.found && versionInfo?.id != null && isPermitted("DP_WORKBENCH");
  const [data,    setData]    = useState(cachedData ?? null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    scenarioService.getDpInitialState()
      .then(r => setData(r))
      .catch(e => setErr(e?.response?.data?.detail || e.message || t("MSG_IpQueryError")))
      .finally(() => setLoading(false));
  }, [t]);

  // 캐시 형태 가드 — 캐시는 redux viewState 에 살아남으므로 백엔드 배포 후에도 구버전
  //   응답이 재사용될 수 있다. trail 이 없는 응답이면 세그먼트 카드·워크리스트 세그먼트 열이
  //   전부 비어 "데이터가 없는 화면"처럼 보이므로 형태를 보고 재조회한다.
  const cacheStale = useMemo(() => {
    const segs = Object.values(cachedData?.risk_summary?.segment_summary || {});
    return segs.length > 0 && !("trail" in segs[0]);
  }, [cachedData]);

  // 캐시가 없거나 구버전일 때만 조회
  useEffect(() => {
    if (cachedData && !cacheStale) return;
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 변경 시 부모에 캐시 전달
  useEffect(() => {
    if (data) onDataChange?.(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // 조회 완료 여부를 부모에 보고 → 상단 액션바(자동 수행 / 시나리오 구성으로) 잠금 해제 기준.
  //   실패(err)도 완료로 본다 — 데이터가 안 와도 사용자가 버튼에 갇히면 안 된다.
  //   마운트 직후 loading=false 구간을 "완료"로 오판하지 않도록 data/err 도착까지 false 를 유지한다.
  useEffect(() => {
    onReadyChange?.(!loading && (!!data || !!err));
  }, [loading, data, err]); // eslint-disable-line react-hooks/exhaustive-deps

  // 최근 실행 기록(빈 상태 패널 우측 요약) — 전 모듈 공용 훅. 미집계일 때만 1건 조회한다.
  //   ⚠️ 훅이라 아래 loading/err early return 보다 앞에 있어야 한다(훅 순서 규칙).
  const lastRun = useLastRunSummary("DP", data?.released_fact_available === false);

  // 월별 판매계획·통계예측·실제판매 추이 — 전사+품목군별 일괄 보유, 드롭다운 로컬 전환(재조회 없음)
  const [trendGroup, setTrendGroup] = useState("");   // "" = 전사 총량
  const selectedTrend = useMemo(() => {
    // 미집계면 추이도 감춘다(전 모듈 공통 원칙) — 훅 순서 규칙상 여기서 직접 판정한다.
    if (data?.released_fact_available === false) return null;
    const td = data?.plan_actual_trend;
    if (!td) return null;
    return trendGroup ? (td.by_group?.[trendGroup] ?? { data: [], series: [] }) : td.all;
  }, [data, trendGroup]);

  // ── 주의 품목 워크리스트 필터/정렬 ──────────────────────────────────────
  const [selectedSegment, setSelectedSegment] = useState(null);        // null = 전체
  const [flagFilter,      setFlagFilter]      = useState(null);        // null|LOW_ACHIEVE|OVER_ADJUST
  const worklistRef = useRef(null);

  const toggleSegment = useCallback((seg) => {
    setSelectedSegment((cur) => {
      const next = cur === seg ? null : seg;
      // md 이상에서는 워크리스트가 바로 우측이라 block:"nearest" 가 no-op 이다.
      //   컬럼이 한 줄로 접히는 xs 에서만 실제로 스크롤된다. 해제 시엔 화면이 튀지 않게 둔다.
      if (next) worklistRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return next;
    });
  }, []);
  // 세그먼트 컨트롤과 동일한 라디오 동작 — 같은 값을 다시 누르면 해제
  //   워크리스트 세그먼트 컨트롤은 해제 시 code=null 을 넘기므로 삼항은 그대로 null 을 반환한다.
  //   삼항이 필요한 쪽은 값을 그대로 넘기는 호출부(좌측 도넛·요약셀·라이프사이클 칩)다.
  const toggleFlag = useCallback((code) => {
    setFlagFilter((cur) => (cur === code ? null : code));
  }, []);

  // ── 행 3 높이 고정: 「필터 없음」 상태의 실측 높이를 바닥으로 잡는다 ──────────
  //   필터를 걸면 워크리스트 행이 5개 미만이 되어 우측 컬럼이 줄고, 같은 grid 행인
  //   좌측 세그먼트 카드까지 따라 줄어든다. 행 높이 산식으로 예약해 봤자 헤더·칩·패딩의
  //   차액을 못 잡아 몇십 px 이 남는다 → 실제 렌더 높이를 재서 그대로 바닥으로 쓴다.
  //   무필터일 때만 갱신하는 게 핵심이다. 필터 중 높이를 바닥으로 삼으면 한 번 줄어든 값이
  //   그대로 굳는다. 무필터 상태의 자연 높이는 항상 바닥과 같으므로 되먹임이 생기지 않는다.
  //   측정 대상을 ref 가 아니라 state 로 들고 있는 이유: StatusSection 을 접으면 CardContent 가
  //   언마운트돼 grid 가 사라진다. ref 였다면 다시 펼쳤을 때 옵저버를 붙일 기회가 없다.
  const [diagGridEl, setDiagGridEl] = useState(null);
  const [diagFloorH, setDiagFloorH] = useState(0);
  const noWorklistFilter = !selectedSegment && !flagFilter;
  useLayoutEffect(() => {
    if (!noWorklistFilter || !diagGridEl) return undefined;
    const update = () => setDiagFloorH((cur) => (
      diagGridEl.offsetHeight !== cur ? diagGridEl.offsetHeight : cur));
    update();
    // 창 너비·언어 변경으로 칩 줄바꿈이 달라지면 무필터 높이 자체가 바뀐다 → 다시 잰다
    const ro = new ResizeObserver(update);
    ro.observe(diagGridEl);
    return () => ro.disconnect();
  }, [noWorklistFilter, data, diagGridEl]);

  // ── 행 2 높이 정합: 우측 정확도 스택을 재서 좌측 추이 차트에 반영 ──────────
  //   단방향(우→좌)만 허용한다. 측정 대상 Box 에 height:100% 를 주면 순환이 생긴다.
  const [chartTrendHeight, setChartTrendHeight] = useState(DP_CHART_MIN_H);
  const accRoRef = useRef(null);
  const measureAccBox = useCallback((el) => {
    // 콜백 ref — StatusSection 이 접히면 CardContent 가 언마운트되어 el=null 로 재호출된다.
    //   여기서 disconnect 하지 않으면 접기/펼치기마다 옵저버가 누적된다.
    if (accRoRef.current) { accRoRef.current.disconnect(); accRoRef.current = null; }
    if (!el) return;
    const update = () => setChartTrendHeight(
      Math.max(DP_CHART_MIN_H, Math.round(el.offsetHeight - DP_CHART_PAD_DIFF_PX)),
    );
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    accRoRef.current = ro;
  }, []);
  useEffect(() => () => accRoRef.current?.disconnect(), []);

  // 세그먼트 라벨/색 — 언어 전환 시에만 재생성
  const segMeta = useMemo(() => buildSegMeta(t), [t]);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: C.primary }} />
            <Typography variant="caption" color="text.secondary">
              {t("MSG_DpStatusAnalyzing")}
            </Typography>
          </Stack>
          <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.danger}30` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 15, color: C.warning }} />
            <Typography variant="caption" color={C.warning}>
              {t("MSG_IpStatusQueryFailed", { err })}
            </Typography>
            <IconButton size="small" onClick={load} sx={{ ml: "auto !important", p: "3px" }}>
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // 미집계 = 릴리즈 fact 미적재. 응답 공통 필드 하나로 판정한다(전 모듈 동일 기준).
  //   ⚠️ 이 값 하나로 화면의 모든 데이터 소스를 일괄 게이트한다 — 일부 섹션만 값이 남으면
  //   "어디는 보이고 어디는 —"인 반쪽 화면이 되어 판정 가능 여부를 오독하게 만든다.
  const dpNotAggregated = data?.released_fact_available === false;

  // 표준 initial-state 골격(flat): kpi_current/risk_summary 최상위, 주요 목록은 highlights
  const kpi       = dpNotAggregated ? {} : (data?.kpi_current || {});
  const groups    = dpNotAggregated ? [] : (data?.item_group_analysis || []);
  const segment   = dpNotAggregated ? {} : (data?.risk_summary?.segment_summary || {});
  const attention = dpNotAggregated ? [] : (data?.highlights || []);
  const hasSeg      = Object.keys(segment).length > 0;
  const hasAccuracy = kpi.mf_vs_sa_mape != null || kpi.bf_vs_sa_mape != null;
  // 제품군 표 정렬: 개선 필요 → 양호 (판정식은 상태 칩과 공유)
  const sortedGroups = [...groups].sort((a, b) => Number(needsImp(b)) - Number(needsImp(a)));
  // 버전 표시: versionInfo(latestVersions) > data.plan_ver_name > plan_ver_id 순 우선

  return (
    <Stack spacing={1.5}>

      {/* 미집계 빈 상태 패널 — 화면에서 유일한 solid CTA(계획 프로세스 관리로 이동) */}
      {dpNotAggregated && (() => {
        const verName   = data?.plan_ver_name ?? versionInfo?.name ?? null;
        const releaseTs = data?.plan_release_ts ?? versionInfo?.releaseTs ?? null;
        return (
          <StatusEmptyPanel
            title={t("statusNoAggregatedResultTitle")}
            desc={verName
              ? t("statusNoAggregatedResultDesc", { name: verName })
              : t("statusNoReleasedVersionDesc")}
            ctaLabel={t("menuDpProcessMgmt")}
            onCta={() => openPlanRunScreen(reduxDispatch, "DP_PROCESS_MGMT")}
            summary={buildStatusPanelSummary(t, {
              verName,
              releaseTs: releaseTs ? new Date(releaseTs).toLocaleDateString() : null,
              lastRun,
            })}
          />
        );
      })()}

      {/* ── 행 1: DP 계획 현황 요약 (전폭 5카드) ──
          화면 상태를 규정하는 지표만 최상단 한 행에 모은다. BF 달성률은 정확도 섹션에서
          이 행으로 승격 — 정확도 섹션에는 '오차율' 두 개만 남는다.
          ⚠️ 5장 모두 footer 없음: 한 장만 footer 가 있으면 그 카드만 높아져 값 baseline 이 어긋난다. */}
      <StatusKpiSection
        collapsible
        defaultOpen
        columns={5}
        versionInfo={versionInfo}
        right={(
          <Stack direction="row" spacing={0.6} alignItems="center">
            {data?.rolling && (
              // 2026-07-10 개편 — 현황은 특정 릴리즈 버전이 아니라 릴리즈 롤링(과거 계획 vs 실적) 기준
              <Chip size="small" label={t("dpRollingStateLabel")}
                sx={{ height: 20, fontSize: FONT.BODY, fontWeight: 700, color: C.teal, bgcolor: C.tealBg,
                      "& .MuiChip-label": { px: "8px" } }} />
            )}
            {/* 열려있는 SCENARIO 버전(시나리오 base) — 롤링 칩과 동일한 Chip 스타일로 표시 */}
            <DpBaseVersionBadge asChip />
          </Stack>
        )}
        cards={kpi.total_items != null ? [
                { label: t("dpAvgAchievementRateLabel"),
                  value: kpi.avg_achievement_rate != null ? kpi.avg_achievement_rate : null,
                  unit: t("percentUnit"),
                  color: achieveColor(kpi.avg_achievement_rate) },
                { label: t("dpBfAchievementRateLabel"),
                  value: kpi.avg_bf_achievement_rate != null ? kpi.avg_bf_achievement_rate : null,
                  unit: t("percentUnit"),
                  color: C.primary },
                { label: t("dpLowAchieveCountLabel"),
                  value: kpi.low_achieve_count != null ? kpi.low_achieve_count : null,
                  unit: t("unitCount"),
                  color: (kpi.low_achieve_count || 0) > 0 ? C.warning : C.success },
                // over_adjusted_count = 백엔드 키. (과거 over_plan_count 를 읽어 항상 0 이었다)
                { label: t("dpOverAdjustItemCountLabel"),
                  value: kpi.over_adjusted_count != null ? kpi.over_adjusted_count : null,
                  unit: t("unitCount"),
                  color: (kpi.over_adjusted_count || 0) > 0 ? C.danger : C.success },
                { label: t("dpTotalItemCountLabel"),
                  value: kpi.total_items != null ? kpi.total_items.toLocaleString() : null,
                  unit: t("unitCount"),
                  color: C.neutral },
              ].map((kp, i) => ({
                key: i,
                label: kp.label,
                value: kp.value,
                unit: kp.unit,
                color: kp.color,
              })) : []}
        empty={kpi.total_items != null ? null : <StatusEmpty text={t("MSG_DpNoCompletedVersion")} />}
      />

      {/* ── 행 2: 월별 판매계획 vs 실적(좌, 2/3) ↔ 계획·예측 정확도(우, 1/3) ──
          추이 차트가 이 화면의 유일한 시계열 근거이므로 좌측 주역으로 둔다.
          JSX 순서 = DOM 순서 = 탭·스크린리더 순서 — CSS order 로 뒤집지 않는다. */}
      <Box sx={{ display: "grid", gap: 1.5, alignItems: "stretch",
        /* 2열 골격 고정 — 정확도 데이터가 없다고 섹션을 지우면 좌측 차트가 전폭으로 튄다.
           자리를 지키고 내부만 스켈레톤으로 채운다(RP·IP 와 동일 규칙). */
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" } }}>

      {/* 월별 판매계획 vs 실적 (좌측) */}
      <StatusSection
        collapsible
        defaultOpen
        cardSx={{ height: "100%" }}
        title={t("dpForecastActualTitle")}
        contentSx={{ p: "14px 15px !important" }}
        // 헤더는 제목 + 접힘 화살표만 — 조회 범위 드롭다운은 본문 최상단 툴바로 내렸다.
        toolbar={
          <StatusToolbar label={t("trendScopeLabel")}>
            <StatusToolbarSelect
              value={trendGroup}
              onChange={setTrendGroup}
              minWidth={150}
              options={[
                { value: "", label: t("dpForecastGroupAll") },
                ...groups.filter(g => g.item_group_cd)
                         .map(g => ({ value: g.item_group_cd, label: g.item_group })),
              ]}
            />
          </StatusToolbar>
        }
      >
        {/* 높이는 우측 정확도 스택 실측값에서 툴바 블록만큼 뺀 값 (측정은 우측이 소유) */}
        <ForecastActualChart trend={selectedTrend} emptyText={t("dpForecastActualEmpty")}
          height={Math.max(DP_CHART_MIN_H - STATUS_TOOLBAR_BLOCK_H,
                           chartTrendHeight - STATUS_TOOLBAR_BLOCK_H)} />
      </StatusSection>

      {/* 계획·예측 정확도 (우측) — 오차율 2개 세로 스택 + 해석 코멘트.
          정확도 값이 없어도 섹션은 자리를 지킨다(카드는 '—', 판정 색 없음). */}
      {(() => {
        const mfErr = kpi.mf_vs_sa_mape;   // 계획 오차율 (낮을수록 정확)
        const bfErr = kpi.bf_vs_sa_mape;   // 예측 오차율
        // 매니저 조정 효과: BF 오차 대비 계획(MF) 오차가 줄었으면 개선
        const improved = (mfErr != null && bfErr != null) ? bfErr - mfErr : null;
        const errColor = (v) => v == null ? C.neutral : v <= 15 ? C.success : v <= 30 ? C.warning : C.danger;
        return (
          <StatusSection
            collapsible
            defaultOpen
            collapseDisabled={!hasAccuracy}
            cardSx={{ height: "100%" }}
            title={t("dpPlanForecastAccuracyTitle")}
            subtitle={t("dpPlanForecastAccuracySubtitle")}
            right={!hasAccuracy ? <StatusPendingTag /> : undefined}
          >
            {/* 이 Box 가 좌측 차트 높이의 기준(측정 대상) — height:100% 를 주면 순환이 생긴다 */}
            <Box ref={measureAccBox}>
            <Stack spacing={1.5}>
              {/* columns=1 → repeat(1,1fr) = 세로 스택. BF 달성률은 행 1 로 승격되어 여기 없다. */}
              <KpiCardGrid
                columns={1}
                cards={[
                  { label: t("dpPlanErrorRateLabel"), value: mfErr != null ? mfErr : null,
                    unit: t("percentUnit"),
                    color: errColor(mfErr), note: t("dpLowerIsPlanAccurateNote") },
                  { label: t("dpForecastErrorRateLabel"), value: bfErr != null ? bfErr : null,
                    unit: t("percentUnit"),
                    color: errColor(bfErr), note: t("dpLowerIsBfAccurateNote") },
                ].map((kp, i) => ({
                  key: i, label: kp.label, value: kp.value,
                  unit: kp.unit,
                  color: kp.color,
                  footer: kp.note,
                }))}
              />
              {improved != null && (() => {
                // 오차 기준값이 크므로(수십 %) 소폭 차이(<15%p)는 "비슷한 수준"으로 보고 색 강조 최소화.
                // 위험(빨강)은 남용하지 않고, MF가 유의미하게 나쁠 때만 주의(주황) 사용.
                const gap = Math.abs(improved);
                const sev = gap < 15 ? "even" : improved > 0 ? "good" : "warn";
                const col = sev === "good" ? C.success : sev === "warn" ? C.warning : C.neutral;
                const bg  = sev === "good" ? C.successBg : sev === "warn" ? C.warningBg : C.neutralBg;
                const msg = sev === "even"
                  ? t("MSG_DpManagerAdjustEven", { improved: gap.toFixed(1) })
                  : improved > 0
                  ? t("MSG_DpManagerAdjustImproved", { improved: gap.toFixed(1) })
                  : t("MSG_DpManagerAdjustWorsened", { improved: gap.toFixed(1) });
                return (
                  <Box sx={{ mt: 1, p: "7px 10px", borderRadius: 1.5,
                    bgcolor: bg, border: `1px solid ${col}30` }}>
                    <Typography fontSize={FONT.BODY} sx={{ color: col, lineHeight: 1.5 }}>
                      {msg}
                    </Typography>
                  </Box>
                );
              })()}
            </Stack>
            </Box>
          </StatusSection>
        );
      })()}
      </Box>

      {/* ── 행 3: 수요추세 진단 — 세그먼트(좌, 1/3) ↔ 주의 품목 워크리스트(우, 2/3) ──
          IP 「문제 항목 TOP 5」(부족·과잉)와 같은 규격: 하나의 카드 안에 소제목 둘.
          세그먼트 카드 선택이 곧 우측 표의 필터라 한 덩어리로 접고 펴는 편이 맞고,
          「칩 클릭 → 표 변화」가 시선 이동 없이 보인다. */}
      {(hasSeg || attention.length > 0) && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("dpDemandDiagnosisTitle")}
          contentSx={{ p: "12px 14px !important" }}
        >
          <Box ref={setDiagGridEl}
            sx={{ display: "grid", gap: 1.5, alignItems: "stretch",
              gridTemplateColumns: { xs: "1fr",
                md: (hasSeg && attention.length > 0) ? "1fr 2fr" : "1fr" },
              // 무필터 실측 높이(위 useLayoutEffect) — 필터로 행이 줄어도 섹션이 안 줄어든다
              minHeight: diagFloorH ? `${diagFloorH}px` : undefined,
              "& > *": { minWidth: 0 } }}>

            {hasSeg && (
              <DpSegmentBlock segment={segment} segMeta={segMeta}
                selectedSegment={selectedSegment} onToggle={toggleSegment} />
            )}

            {attention.length > 0 && (
              <DpWorklistBlock
                items={attention}
                segment={segment}
                segMeta={segMeta}
                selectedSegment={selectedSegment}
                onToggleSegment={toggleSegment}
                flagFilter={flagFilter}
                onToggleFlag={toggleFlag}
                navOk={navOk}
                onNavItem={goToWorkbenchByItem}
                sectionRef={worklistRef} />
            )}
          </Box>
        </StatusSection>
      )}

      {/* ── 행 4: 제품군별 계획 현황 (전체 폭) — 개선 필요 → 양호 순 ──
          품목 단위 진단(행 3)을 끝낸 뒤 보는 롤업이라 아래로 내린다. */}
      {groups.length > 0 && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("dpGroupStatusImproveTitle")}
          contentSx={{ p: "12px 14px !important" }}
          right={(
            <Chip label={t("dpGroupCountLabel", { count: groups.length })} size="small"
              sx={{ height: 20, fontSize: FONT.CAPTION, bgcolor: C.purpleBg, color: C.purple }} />
          )}
        >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <StatusItemList>
              <StatusItemHeader
                nameLabel={`${t("itemGroupLabel")} / ${t("itemCount")}`}
                rightW={GRP_CHIP_W}
                barValue={{ w: 100, label: t("achievementRateLabel"), unit: "%" }}
                cells={[
                  { w: GRP_MAPE_W,    label: t("dpGroupMapeColLabel"), unit: "%" },
                  { w: GRP_TREND_W,   label: t("trend"), unit: "%" },
                  { w: GRP_INSIGHT_W, label: t("dpGroupInsightColLabel"), align: "left" },
                ]} />
              <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
                {sortedGroups.map((grp, idx) => {
                  const needsImprove = needsImp(grp);
                  const ac = achieveColor(grp.avg_achievement || 0);
                  const groupNav = navOk && (grp.item_group_cd || grp.item_group);
                  // 권고 문구(추세·달성률 기반)는 이름 아래 인라인이 아니라 전용 컬럼으로 뺀다 —
                  //   행마다 길이가 달라 이름 줄이 들쭉날쭉했고, 눈으로 세로 비교도 안 됐다
                  const grpInsight = (Array.isArray(grp.insight_parts) && grp.insight_parts.length > 0)
                    ? grp.insight_parts.map((p) => t(p.key, p.args)).join(" · ")
                    : (grp.insight || "");
                  const grpCountLabel = t("dpGroupItemCountLabel", { count: grp.item_count || 0 });
                  const tc = grp.trend === "up" ? C.success : grp.trend === "down" ? C.danger : C.neutral;
                  return (
                    <StatusItemRow key={idx}
                      accent={ac}
                      name={<NavName targets={[
                        { label: i18n.t("menuDpPractitionerWorkbench"),
                          onClick: groupNav ? () => goToWorkbenchByGroup(grp) : undefined },
                      ]}>{grp.item_group}</NavName>}
                      sub={grpCountLabel}
                      bar={{ value: Math.min(Math.max(grp.avg_achievement || 0, 0), 100), color: ac }}
                      barValue={{ w: 100, unit: grp.avg_achievement != null ? "%" : "", num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: ac, lineHeight: 1 }}>
                          {grp.avg_achievement != null ? grp.avg_achievement : "—"}
                        </Typography>
                      ) }}
                      cells={[
                        // 예측 오차(MAPE) — 낮을수록 정확. 없으면 —
                        { w: GRP_MAPE_W, unit: grp.mape != null ? "%" : "", num: (
                          <Typography sx={{ fontSize: FONT.SUB, fontWeight: grp.mape != null ? 700 : 400,
                                            lineHeight: 1, color: C.neutral,
                                            fontVariantNumeric: "tabular-nums" }}>
                            {grp.mape != null ? grp.mape : "—"}
                          </Typography>
                        ) },
                        // 추세 — 아이콘 + 증감률(없으면 성장/둔화/보합 문구).
                        //   증감률이 있는 행끼리는 % 시작점이 맞고, 문구 폴백 행은 숫자 열 끝에 정렬돼
                        //   "숫자 자리에 텍스트"임이 드러난다. 아이콘은 고정폭이라 숫자 앞에 그대로 둔다
                        { w: GRP_TREND_W,
                          unit: grp.trend && grp.growth_rate_pct != null ? "%" : "",
                          num: grp.trend ? (
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.2}>
                            {grp.trend === "up"
                              ? <TrendingUpIcon sx={{ fontSize: 15, color: tc }} />
                              : grp.trend === "down"
                              ? <TrendingDownIcon sx={{ fontSize: 15, color: tc }} />
                              : <RemoveIcon sx={{ fontSize: 15, color: tc }} />}
                            <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, lineHeight: 1, color: tc, whiteSpace: "nowrap" }}>
                              {grp.growth_rate_pct != null
                                ? `${grp.growth_rate_pct > 0 ? "+" : ""}${grp.growth_rate_pct}`
                                : (grp.trend === "up" ? t("growthLabel") : grp.trend === "down" ? t("shrinking") : t("stableLabel"))}
                            </Typography>
                          </Stack>
                        ) : "—" },
                        // 검토 사항 — 백엔드 권고(계획 상향/하향·성장 여지·과조정 건수). 양호하면 없다
                        { w: GRP_INSIGHT_W, align: "left", node: (
                          <Typography title={grpInsight || undefined}
                            sx={{ fontSize: FONT.BODY, lineHeight: 1, whiteSpace: "nowrap",
                                  overflow: "hidden", textOverflow: "ellipsis",
                                  color: grpInsight ? C.warning : C.neutral,
                                  fontWeight: grpInsight ? 700 : 400 }}>
                            {grpInsight || "—"}
                          </Typography>
                        ) },
                      ]}
                      rightW={GRP_CHIP_W}
                      right={(
                        // 폭은 칩 내용 그대로 — 열 폭이 실측 최대값으로 맞춰지므로 칩을 고정폭으로
                        //   묶을 필요가 없다(고정폭이면 영어 'Needs Improvement' 가 칩 안에서 잘렸다)
                        <Chip label={needsImprove ? t("dpNeedsImproveLabel") : t("goodStatusLabel")} size="small"
                          sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700, flexShrink: 0,
                            "& .MuiChip-label": { px: "6px" },
                            bgcolor: needsImprove ? C.warningBg : C.successBg,
                            color:   needsImprove ? C.warning   : C.success }} />
                      )} />
                  );
                })}
              </Stack>
              </StatusItemList>
            </Box>
        </StatusSection>
      )}

      {/* ── 섹션 3: AI 인사이트 요약 — ai_insight_lines(신규) 우선, 없으면 ai_insights 문자열 폴백 ── */}
      {(Array.isArray(data?.ai_insight_lines) && data.ai_insight_lines.length > 0
        ? true
        : !!data?.ai_insights) && (
        <StatusSection
          collapsible
          defaultOpen
          title={t("aiInsightDirectionTitle")}
          contentSx={{ p: "12px 14px !important" }}
        >
            <Typography variant="caption" fontSize={FONT.BODY} color="text.secondary" lineHeight={1.6}
              sx={{ whiteSpace: "pre-line" }}>
              {Array.isArray(data.ai_insight_lines) && data.ai_insight_lines.length > 0
                ? data.ai_insight_lines.map((line) => `• ${t(line.key, line.args)}`).join("\n")
                : data.ai_insights}
            </Typography>
        </StatusSection>
      )}

    </Stack>
  );
}

// ── DP_MODULE에 ExtraInitContent 등록 ──────────────────────────────────────

// 시나리오 카드 미리보기의 파라미터 값 포맷 (공통 화면이 mod.formatParamValue 로 호출)
const DP_INIT_DEMAND_LABEL = {
  baselineForecast:    "dpInitDemandBaselineForecast",
  lastSalesActual:     "dpInitDemandLastSalesActual",
  salesActualYoY:      "dpInitDemandSalesActualYoY",
  lastReleasedVersion: "dpInitDemandLastReleasedVersion",
  financePlan:         "dpInitDemandFinancePlan",
  salesPlanMeasure:    "dpInitDemandSalesPlanMeasure",
  rollingRange:        "dpInitDemandRollingRange",
};

// ── DP 모듈 설정 (AiScenarioPlanning 이 import 하는 실제 데이터) ──────────
export const DP_MODULE = {
  code: "DP",
  label: "dpModuleLabel",
  Icon: TrendingUpIcon,
  step2lbl: "dpStep2Label",
  step4lbl: "dpStep4Label",
  inputFrom: "BF",
  hideParamToggle: true, // 파라미터 엔진 실행 단계에서 사용여부 체크박스 숨김 (모든 파라미터 항상 적용)
  skipKpiSection: true,  // 현황(step0)의 빈 kpiMeta StepKpiAnalysis 생략 — DpExtraInitContent가 실데이터 표시

  // 시나리오 미리보기 파라미터 라벨/포맷 (DP scenario.params 키는 mod.params 와 일부 다름)
  // 값은 i18n 키 문자열 — 공용 AiScenarioRecCards 의 DP renderPreviewBody 가 소비(설정 요약).
  paramLabels: {
    initial_demand:          "dpInitialDemandParamLabel",
    compute_measure_formula: "dpMfQtyFormulaParamLabel",
    planning_range:          "dpPlanningRangeParamLabel",
    bucket:                  "dpBucketParamLabel",
  },
  // 컴포넌트가 아닌 모듈 레벨 함수라 i18n 싱글턴으로 번역한다(현재 소비처 없음, 향후 대비).
  formatParamValue(k, v) {
    if (k === "compute_measure_formula") {
      if (!Array.isArray(v) || v.length === 0) return i18n.t("dpFormulaUnchangedLabel");
      return v.map(f => `= ${f.MF_QTY}`).join(", ");
    }
    if (k === "planning_range") return i18n.t("dpMonthsValueLabel", { v });
    if (k === "bucket")         return v === "WEEK" ? i18n.t("dpWeekBucketLabel") : i18n.t("dpMonthBucketLabel");
    if (k === "initial_demand") return DP_INIT_DEMAND_LABEL[v] ? i18n.t(DP_INIT_DEMAND_LABEL[v]) : v;
    return String(v);
  },
  // scenarios / kpis / bestMap / aiRec → Agent 동적 반환 (하드코딩 금지)
  // 아래는 UI 렌더링 fallback용 최솟값 (Agent 결과 수신 전 표시)
  scenarios: [
    {
      role: "base", name: "scnDpBaseName",
      desc: "scnDpBaseDesc",
      tags: ["baselineForecast", "scnTagStandard"],
      params: { initial_demand: "baselineForecast", compute_measure_formula: [{ MF_QTY: "BF_QTY" }], planning_range: 18, bucket: "MONTH" },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
    {
      role: "alt1", name: "scnDpAlt1Name",
      desc: "scnDpAlt1Desc",
      tags: ["lastSalesActual", "scnTagConservative"],
      params: { initial_demand: "lastSalesActual", compute_measure_formula: [], planning_range: 18, bucket: "MONTH" },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
    {
      role: "alt2", name: "scnDpAlt2Name",
      desc: "scnDpAlt2Desc",
      tags: ["baselineForecast", "+10%"],
      params: { initial_demand: "baselineForecast", compute_measure_formula: [{ MF_QTY: "BF_QTY * 1.1" }], planning_range: 18, bucket: "MONTH" },
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-" },
    },
  ],
  // 값은 i18n 키 문자열 — 레거시 제너릭 폴백(mod.CustomScenarioFields 우선 적용으로 현재 렌더 미사용).
  //   향후 대비 키만 등록(라벨맵 전면 전환 정책).
  params: [
    { k: "dpInitialDemandParamLabel", paramKey: "initial_demand", type: "select",
      options: [
        { value: "baselineForecast",    label: "dpInitDemandBaselineForecast" },
        { value: "lastSalesActual",     label: "dpInitDemandLastSalesActual" },
        { value: "salesActualYoY",      label: "dpInitDemandSalesActualYoYOption" },
        { value: "financePlan",         label: "dpInitDemandFinancePlan" },
        { value: "lastReleasedVersion", label: "dpInitDemandLastReleasedVersion" },
      ],
      vals: { base: "baselineForecast", alt1: "lastSalesActual", alt2: "baselineForecast" } },
    { k: "dpPlanningRangeMonthsParamLabel", paramKey: "planning_range", type: "number",
      vals: { base: "18", alt1: "18", alt2: "18" } },
    { k: "dpBucketParamLabel", paramKey: "bucket", type: "select",
      options: [
        { value: "MONTH", label: "dpMonthBucketLabel" },
        { value: "WEEK",  label: "dpWeekBucketLabel"  },
      ],
      vals: { base: "MONTH", alt1: "MONTH", alt2: "MONTH" } },
    { k: "dpMfQtyAdjustFormulaParamLabel", paramKey: "mf_qty_formula", type: "text",
      vals: { base: "BF_QTY", alt1: "SA_QTY", alt2: "BF_QTY * 1.1" } },
  ],
  // 비교 구간 = 릴리즈 계획 전체(4월~9월). 하향은 미래월(현재월~)만 적용(확정 시 과거 미변경).
  // → 과거(4·5월) base=alt 동일이라 달성률(과거 기준)은 변별력 없어 비교표 제외(현황 분석 탭 참조).
  // 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t(lbl) 형태로 변환한다.
  kpiLabels: ["dpKpiPlanQtyLabel", "dpKpiPlanAmtLabel", "dpKpiVsActualGapLabel",
              "dpKpiOverPlanCountLabel", "dpKpiUnderPlanCountLabel", "dpKpiAdjustCountLabel"],
  kpiKeys:   ["a", "b", "c", "e", "f", "g"],
  // 방향성: 과부족·과다·과소는 낮을수록 건전. 계획수량·금액·조정은 neutral(종합점수로 판정).
  kpiDir:    { a: "neutral", b: "neutral", c: "min", e: "min", f: "min", g: "neutral" },
  bestMap:   {},  // dir 기반 자동 산출
  // label/insight/note 값은 i18n 키 문자열 — 소비처(StepKpiAnalysis)에서 t()로 변환한다.
  kpiMeta: [
    {
      key: "a", icon: "", label: "dpKpiPlanQtyLabel",
      getStatus: () => "neutral",
      insight: "dpKpiInsightPlanQty",
      note: "dpKpiNotePlanQty",
    },
    {
      key: "b", icon: "", label: "dpKpiPlanAmtLabel",
      getStatus: () => "neutral",
      insight: "dpKpiInsightPlanAmt",
      note: "dpKpiNotePlanAmt",
    },
    {
      key: "c", icon: "", label: "dpKpiVsActualGapLabel",
      getStatus: (v) => {
        const s = String(v);
        if (s.includes("비슷")) return "good";
        const n = parseFloat(s.replace(/[^0-9.]/g, ""));
        if (isNaN(n)) return "neutral";
        if (s.includes("부족")) return "warn";        // 과소 = 결품 위험
        return n <= 5 ? "good" : n <= 15 ? "near" : "warn";   // 과잉 정도
      },
      insight: "dpKpiInsightVsActualGap",
      note: "dpKpiNoteVsActualGap",
    },
    {
      key: "e", icon: "", label: "dpKpiOverPlanCountLabel",
      getStatus: (v) => (v === "-" || v === "—") ? "neutral" : "near",
      insight: "dpKpiInsightOverPlan",
      note: "fewerIsBetterNote",
    },
    {
      key: "f", icon: "", label: "dpKpiUnderPlanCountLabel",
      getStatus: (v) => (v === "-" || v === "—") ? "neutral" : "near",
      insight: "dpKpiInsightUnderPlan",
      note: "fewerIsBetterNote",
    },
    {
      key: "g", icon: "", label: "dpKpiAdjustCountLabel",
      getStatus: () => "neutral",
      insight: "dpKpiInsightAdjustCount",
      note: "dpKpiAdjustCountLabel",
    },
  ],
  // 흐름도 배선 — 부모 래퍼 단계가 없어 분기 앵커를 합성한다.
  //   tool 에 role 이 없고 label_args.cd(scenario_cd)로만 시나리오가 갈린다.
  //   백엔드가 base 를 엔진에서 빼므로(dp/orchestrator.py) 트랙에서도 빠진다.
  //   flowTopNodes = 공통 3(현황·시나리오 확정·KPI 해석) + 합성 앵커 1.
  engineTrack: { parentTool: null, childPrefix: "dp_multi_alt", match: "cd", flowTopNodes: 4 },
  // 값은 i18n 키 문자열 — 현재 소비처 없음(mod.enginePhases 미사용). 향후 대비 키만 등록.
  enginePhases: [
    "dpEnginePhaseBfLoading",
    "dpEnginePhaseParamAnalysis",
    "dpEnginePhaseVersionCreate",
    "dpEnginePhaseInitDemand",
    "dpEnginePhaseMfQtyApply",
    "dpEnginePhaseKpiAggregate",
    "complete",
  ],
  // ── Analysis 탭 Quick Prompt (자유 질문, 액션 없음) — { i18nKey, ko } ──────────
  // ko = l10n 미등록 시 폴백 라벨. 다국어는 translation.*.json 의 plannelAgent 키로 마지막에 채운다.
  quickPrompts: {
    analysis: {
      0: [
        { i18nKey: "agentAskDpKpi",        ko: "현황 KPI 해석해줘" },
        { i18nKey: "agentAskDpImproveSeg", ko: "개선이 필요한 제품군은?" },
        { i18nKey: "agentAskDpOverAdjust", ko: "과조정 품목 알려줘" },
      ],
      1: [
        { i18nKey: "agentAskDpInitMethod", ko: "어떤 초기화 방식이 좋아?" },
        { i18nKey: "agentAskDpParamDiff",  ko: "각 시나리오 파라미터 차이는?" },
        { i18nKey: "agentAskDpRisk",       ko: "이 설정의 리스크는?" },
      ],
      2: [
        { i18nKey: "agentAskDpBest",       ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "agentAskDpScoreBasis", ko: "KPI 종합점수 기준은?" },
      ],
      3: [
        { i18nKey: "agentAskDpChecklist",  ko: "최종 확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode·aiResult)에 맞는 "지금 가능한 액션"만 선언 ──
  // 레지스트리(AgentActions)가 이걸 등록 → 칩·자유입력이 동일 목록을 본다. step 이 아니라
  // 실제 하위상태에 일치하므로 "추천했는데 안 됨"이 구조적으로 없다. run 은 컨테이너 executeAction 재사용.
  // (AI 추천 "생성/재생성"은 startAiAnalysis 를 가진 StepScenarioCreate 가 별도로 등록한다.)
  agentActions: ({ step, mode, aiResult, exec }) => {
    // agentActions는 모듈 레벨 함수(컴포넌트 아님)라 useTranslation 훅을 쓸 수 없어
    // i18n 싱글턴 인스턴스로 직접 번역한다(항상 현재 언어를 반영, IP 와 동일 패턴).
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `dp.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    // 값이 고정된 파라미터 프리셋은 제거했다 — 그 수치의 근거가 없고 사용자가 원하는 값이라는
    //   보장도 없다. 파라미터 변경은 컨테이너 공통 scn.param.change(연속 선택)가 받는다.
    const runEngine = {
      id: "dp.engine.run", i18nKey: "agentActDpRunEngine", ko: "엔진 실행",
      group: "action", style: "primary", description: i18n.t("dpRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_IpEngineRunConfirm"),
    };
    if (step === 0) return [
      selMode("ai",     "agentActDpModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActDpModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // mode 미설정(null)은 표준 시나리오 선택 상태와 동일(통합 뷰 기본) — 초기 방식 선택 액션 없이
      //   표준 경로 액션을 그대로 노출. AI 전환은 컨테이너 공통 scn.mode.ai 가 제공.
      // 방식=ai 인데 생성 전 → 파라미터·엔진은 무의미. 생성 액션(StepScenarioCreate)만 노출.
      if (mode === "ai" && !aiResult) return [];
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 generic nav(nav.step*)가 전담 — 모듈별 고정 navTo 는 라벨 중복이라 제거
  },
  aiRec: 0,

  // AI 추천 시나리오 안내 — DP는 BF 예측값·판매실적·성장률·과조정·승인 마케팅 신호를 분석(재고/리드타임 미사용),
  //   초기화 방식·수요 배율 시나리오를 추천한다.
  // 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  aiRecCardDesc: "dpAiRecCardDesc",
  aiRecIntroLines: [
    "dpAiRecIntroLine1",
    "dpAiRecIntroLine2",
  ],
  aiRecFactors: ["dpAiRecFactorBfForecast", "dpAiRecFactorSalesGrowth", "dpAiRecFactorOverAdjustMape", "dpAiRecFactorApprovedMarketing"],
  aiRecLoadingDesc: "dpAiRecLoadingDesc",
  // agentActDpModeAi 의 ko("AI 추천 시나리오 생성")와 동일 문구라 동일 키를 재사용한다(IP 와 동일 패턴).
  aiRecButtonLabel: "agentActDpModeAi",
};

// DP_MODULE에 ExtraInitContent 등록 (선언 후에 할당)
DP_MODULE.ExtraInitContent = DpExtraInitContent;

// ── 시나리오 구성 상단: 열려있는 SCENARIO 버전 가드 (2026-07-10 DP 개편) ─────────
// DP 시나리오의 base 는 최근 릴리즈 버전이 아니라 **열려있는(진행 중) SCENARIO 버전**이다.
// 열린 버전이 없으면 분석/엔진 실행이 불가하므로 구성 단계 상단에서 버전 생성을 유도한다.
//   - 생성은 기존 DpVersionModal 재사용 — 초기값 등 기본값은 DP 설정(z_plan_config)을
//     모달이 로드해 그대로 프리필한다.
//   - DP 설정의 초기값이 기준 예측(BF)이면 BF 생성·실행까지 필요해 오래 걸린다는 경고 표시.
//   - 생성 후 수요 적재(makeDemand)까지 완료해야 SCENARIO 버전으로 조회된다.
function DpOpenVersionGuard() {
  const { t } = useTranslation();
  const [chk, setChk] = useState({ loading: true, openVer: null, bfInit: false });
  const [modalOpen, setModalOpen] = useState(false);
  const [applying, setApplying] = useState(false);   // 수요 적재 진행 중
  const [snack, setSnack] = useState(null);          // { sev, txt }
  const userRoles = (authService.getCurrentUser()?.roles) || [];

  const check = useCallback(async () => {
    try {
      const res = await planVersionService.getAll({ moduleCd: "DP", statusCds: ["SCENARIO"] });
      const results = res?.data?.results ?? [];
      // AI 시뮬 격리 버전(delFlg='Y')은 base 가 아니다
      const openVer = (Array.isArray(results) ? results : []).find(v => (v.delFlg ?? "N") !== "Y") || null;
      let bfInit = false;
      if (!openVer) {
        try {
          const cfg = await planConfigService.getDpPlanConfig();
          const init = cfg?.data?.data?.planConfigObj?.INITIAL_DEMAND;
          // 신형(initialPlan: 'baselineForecast')·구형([BF_QTY] 수식) 모두 BF 초기값으로 판정
          bfInit = /baselineForecast|\[BF_QTY\]/.test(JSON.stringify(init ?? {}));
        } catch { /* 설정 미조회 시 경고만 생략 */ }
      }
      setChk({ loading: false, openVer, bfInit });
    } catch (e) {
      setChk({ loading: false, openVer: null, bfInit: false });
    }
  }, []);
  useEffect(() => { check(); }, [check]);

  // 열린 버전이 있으면 표시 없음 — 기준 버전명은 하단 "기준 시나리오" 행의
  // 배지(DpBaseVersionBadge, mod.BaseScenarioBadgeExtra)로 노출한다.
  if (chk.loading || chk.openVer) return null;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, p: "12px 16px",
                 bgcolor: C.warningBg, border: `1px solid ${C.warning}55`, borderRadius: "10px" }}>
        <WarningAmberIcon sx={{ fontSize: 20, color: C.warning, flexShrink: 0, mt: "1px" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.warning }}>
            {t("dpOpenVerMissingTitle")}
          </Typography>
          <Typography sx={{ fontSize: FONT.SUB, color: "#7a5410", mt: 0.3 }}>
            {t("dpOpenVerMissingDesc")}
          </Typography>
          {chk.bfInit && (
            <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.danger, mt: 0.5 }}>
              {t("dpOpenVerBfInitWarning")}
            </Typography>
          )}
        </Box>
        <Button variant="contained" size="small" disabled={applying}
          startIcon={applying ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : null}
          onClick={() => setModalOpen(true)}
          sx={{ flexShrink: 0, fontSize: FONT.SUB, py: "6px", px: 1.5, bgcolor: C.warning,
                textTransform: "none", borderRadius: "8px", "&:hover": { bgcolor: "#9c6213" },
                alignSelf: "center" }}>
          {applying ? t("dpOpenVerApplying") : t("dpOpenVerCreateBtn")}
        </Button>
      </Box>
      {modalOpen && (
        <DpVersionModal
          open={modalOpen}
          close={() => setModalOpen(false)}
          userRoles={userRoles}
          running={() => {}}
          reload={() => {}}
          selectVersion={async (createdVersion) => {
            // 버전 생성 완료 → 수요 적재(완료 대기) → 재확인. 확정 플로우(AiScenarioPlanning
            // dpVerModal)와 동일 순서 — 적재가 끝나야 SCENARIO 상태로 base 조회가 가능하다.
            try {
              setApplying(true);
              // INITIAL_DEMAND 는 layout(사용자 정의) 형태로 변환해 전송 — 엔진 #9678 회귀
              // (레거시 맵의 키→initialDemand 해석 삭제)와 무관하게 동작 (확정 모달과 동일 처리).
              await planVersionService.makeDemand(withDpLayoutInitialDemand(createdVersion));
              setSnack({ sev: "success", txt: t("dpOpenVerCreated", { name: createdVersion?.name || "" }) });
              // 기준 시나리오 행의 버전 배지(DpBaseVersionBadge)도 즉시 갱신되도록 알림
              window.dispatchEvent(new CustomEvent("plannel:dpOpenVerChanged"));
            } catch (e) {
              setSnack({ sev: "error",
                txt: t("MSG_VersionCreateApplyFailed", { detail: e?.response?.data?.detail || e?.message || "" }) });
            } finally {
              setApplying(false);
              check();
            }
          }}
        />
      )}
      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack?.sev || "info"} onClose={() => setSnack(null)} sx={{ fontSize: FONT.SUB }}>
          {snack?.txt}
        </Alert>
      </Snackbar>
    </>
  );
}
DP_MODULE.ComposeExtraContent = DpOpenVersionGuard;

// ── 기준 시나리오 행 배지: 열려있는 SCENARIO 버전명 ─────────────────────────────
// "계획 파라미터 · 엔진 실행"의 기준 시나리오(현행 기준) 접이식 행 헤더에 base 버전명을
// 표시한다(mod.BaseScenarioBadgeExtra 슬롯). 시나리오가 어느 버전 기준으로 시뮬레이션되는지
// 카드 자리에서 바로 보이게 — 버전 생성(가드) 직후에는 plannel:dpOpenVerChanged 로 갱신.
function DpBaseVersionBadge({ asChip = false }) {
  const { t } = useTranslation();
  const [verName, setVerName] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await planVersionService.getAll({ moduleCd: "DP", statusCds: ["SCENARIO"] });
      const results = res?.data?.results ?? [];
      const openVer = (Array.isArray(results) ? results : []).find(v => (v.delFlg ?? "N") !== "Y") || null;
      setVerName(openVer?.name || null);
    } catch { setVerName(null); }
  }, []);
  useEffect(() => {
    load();
    window.addEventListener("plannel:dpOpenVerChanged", load);
    return () => window.removeEventListener("plannel:dpOpenVerChanged", load);
  }, [load]);

  if (!verName) return null;
  // asChip: 현황 헤더용 — 옆의 "릴리즈 롤링" 칩(MUI Chip)과 동일한 스타일. 문구는 "현재 시나리오 버전"
  // (기준(base) 표현은 구성 단계의 기준 시나리오 행에서만), 툴팁 없음.
  if (asChip) {
    return (
      <Chip size="small" label={`${t("dpCurrentScenarioVerLabel")} ${verName}`}
        sx={{ height: 20, fontSize: FONT.BODY, fontWeight: 700, color: C.primary, bgcolor: C.primaryBg,
              "& .MuiChip-label": { px: "8px" } }} />
    );
  }
  return (
    <Tooltip title={t("dpOpenVerBaseDesc")} placement="top" arrow>
      <Box component="span" onClick={(e) => e.stopPropagation()}
        sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.primary, bgcolor: C.primaryBg,
              border: `1px solid ${C.primaryL}`, borderRadius: "999px", px: 0.8, py: "1px",
              lineHeight: 1.5, whiteSpace: "nowrap", cursor: "default" }}>
        {t("dpOpenVerBaseLabel")} {verName}
      </Box>
    </Tooltip>
  );
}
DP_MODULE.BaseScenarioBadgeExtra = DpBaseVersionBadge;

// 거래처 계층 검색형 트리 드롭다운 — 트리거는 기존 셀렉트와 동일 크기.
// 데이터는 기존 백엔드 API(customerHrchyService.getAllByFilter)만 재사용하고,
// 펼치면 검색창 + 체크박스 트리. 검색 시 일치 노드와 상위 경로만 표시·자동 펼침 →
// 계층이 깊어도 타이핑으로 즉시 탐색 가능.
// 값은 항상 hrchyCd 배열로 저장(백엔드 fetch_exclude_kpi 가 cust_lv1~5_cd IN 으로 매칭 →
// 상위 노드 선택 = 하위 전체 제외).
// (export) 시나리오 관리 화면(ScenarioManage)의 custHrchy 파라미터 입력에서도 재사용.
// 거래처 계층 코드 → 이름 맵. 접힌 파라미터 행이 코드(HR-001) 대신 이름을 보여 주려면
//   선택기(DpCustHrchySelect) 밖에서도 이름이 필요하다. 응답이 카드 수만큼 반복되지 않도록
//   모듈 캐시에 한 번만 담는다.
let _dpHrchyNameCache = null;
function useDpHrchyNames() {
  const [map, setMap] = useState(_dpHrchyNameCache);
  useEffect(() => {
    if (_dpHrchyNameCache) return;
    let alive = true;
    customerHrchyService.getAllByFilter()
      .then(res => {
        const m = {};
        (res?.data?.results || []).forEach(r => { if (r?.hrchyCd) m[r.hrchyCd] = r.name; });
        _dpHrchyNameCache = m;
        if (alive) setMap(m);
      })
      .catch(() => { /* 이름 조회 실패는 코드 표시로 폴백 */ });
    return () => { alive = false; };
  }, []);
  return map;
}

export function DpCustHrchySelect({ value, multi, disabled, accent, inputSx, onChange }) {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);   // {code, name, parentCd, lv}
  const [anchorEl, setAnchorEl] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const triggerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    customerHrchyService.getAllByFilter()
      .then(res => {
        const results = (res?.data?.results || []).filter(r => r && r.hrchyConfig);
        results.sort((a, b) => (a.hrchyCd < b.hrchyCd ? -1 : a.hrchyCd > b.hrchyCd ? 1 : 0));
        if (alive) setNodes(results.map(r => ({
          code: r.hrchyCd, name: r.name,
          parentCd: r.parentHrchyCd || r.parentHrchy?.hrchyCd || null,
          lv: (r.hrchyConfig?.hrchyNum ?? r.hrchyLv ?? 0),
        })));
      })
      .catch(() => { if (alive) setNodes([]); });
    return () => { alive = false; };
  }, []);

  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const nameOf = (code) => nodes.find(n => n.code === code)?.name || code;

  const childrenOf = useMemo(() => {
    const m = {};
    nodes.forEach(n => { (m[n.parentCd || "__root"] ||= []).push(n); });
    return m;
  }, [nodes]);
  const roots = childrenOf["__root"] || nodes.filter(n => !n.parentCd || !nodes.some(x => x.code === n.parentCd));

  // 검색: 일치 노드 + 상위 경로만 노출, 경로는 자동 펼침.
  const { visible, autoExp } = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return { visible: null, autoExp: null };
    const keep = new Set(), exp = new Set();
    nodes.forEach(n => {
      if (!n.name.toLowerCase().includes(term)) return;
      keep.add(n.code);
      let p = n.parentCd;
      while (p) {
        keep.add(p); exp.add(p);
        const cur = p;
        p = nodes.find(x => x.code === cur)?.parentCd;
      }
    });
    return { visible: keep, autoExp: exp };
  }, [q, nodes]);

  const toggleSel = (code) => {
    if (!multi) { onChange([code]); setAnchorEl(null); return; }
    const s = new Set(selected);
    s.has(code) ? s.delete(code) : s.add(code);
    onChange([...s]);
  };
  const toggleExp = (code) => setExpanded(s => {
    const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n;
  });

  const renderNode = (node, depth) => {
    if (visible && !visible.has(node.code)) return null;
    const kids = childrenOf[node.code] || [];
    const open = (autoExp && autoExp.has(node.code)) || expanded.has(node.code);
    const checked = selected.indexOf(node.code) > -1;
    return (
      <Box key={node.code}>
        <Box sx={{ display: "flex", alignItems: "center", pl: `${depth * 14}px`,
                   borderRadius: 1, "&:hover": { bgcolor: `${accent}0A` } }}>
          {kids.length > 0 ? (
            <IconButton size="small" onClick={() => toggleExp(node.code)}
              sx={{ p: 0.2, flex: "0 0 auto", color: accent,
                    "&:hover": { bgcolor: `${accent}1A` } }}>
              {open ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                    : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          ) : <Box component="span" sx={{ width: 22, flex: "0 0 auto" }} />}
          <Checkbox size="small" checked={checked} sx={{ p: 0.4 }}
            onChange={() => toggleSel(node.code)} />
          <Typography onClick={() => toggleSel(node.code)}
            sx={{ fontSize: FONT.BODY, color: "#111827", cursor: "pointer", py: 0.2,
                  flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {node.name}
          </Typography>
        </Box>
        {open && kids.map(c => renderNode(c, depth + 1))}
      </Box>
    );
  };

  return (
    <>
      <Box ref={triggerRef}
        onClick={() => { if (!disabled) setAnchorEl(triggerRef.current); }}
        sx={{ ...inputSx, display: "flex", alignItems: "center", gap: 0.5,
              cursor: disabled ? "not-allowed" : "pointer",
              ...(anchorEl ? { borderColor: accent } : {}) }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap",
              color: selected.length ? "#111827" : "#9CA3AF" }}>
          {selected.length ? selected.map(nameOf).join(", ") : t("dpCustHrchySelectPlaceholder")}
        </Box>
        <Box component="span" sx={{ flex: "0 0 auto", color: "#9CA3AF", fontSize: 11,
              transform: anchorEl ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
      </Box>
      <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, width: triggerRef.current?.offsetWidth || 260,
              minWidth: 240, maxWidth: 420, borderRadius: 2, boxShadow: 3 } } }}>
        <Box sx={{ p: "8px" }}>
          <Box component="input" autoFocus value={q} placeholder={t("dpCustSearchPlaceholder")}
            onChange={e => setQ(e.target.value)}
            sx={{ ...inputSx, mb: "6px" }} />
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {nodes.length === 0
              ? <Typography sx={{ fontSize: FONT.CAPTION, color: "#9CA3AF", p: 1 }}>{t("dpLoadingEllipsisLabel")}</Typography>
              : roots.map(r => renderNode(r, 0))}
            {visible && visible.size === 0 && (
              <Typography sx={{ fontSize: FONT.CAPTION, color: "#9CA3AF", p: 1 }}>{t("dpNoMatchingCustLabel")}</Typography>
            )}
          </Box>
          {multi && selected.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                       mt: "6px", pt: "6px", borderTop: "0.5px dashed #E5E7EB" }}>
              <Typography sx={{ fontSize: FONT.CAPTION, color: "#6B7280" }}>{t("dpSelectedCountLabel", { count: selected.length })}</Typography>
              <Typography onClick={() => onChange([])}
                sx={{ fontSize: FONT.CAPTION, color: accent, cursor: "pointer", fontWeight: 700 }}>{t("clearAll")}</Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}

// DB 표준 시나리오(param_defs 보유) 평면 파라미터 렌더 — DP 전용.
// RP/IP 의 override+scope(거점/품목) 모델은 DP 에 맞지 않아(거점 용어·problem_targets 공백)
// 공용 ScenarioParamDefsFields 대신 param_defs 를 그대로 평면 입력으로 렌더한다.
function DpParamDefsFields({ scenario, accent, disabled, onParamChange, view = "all", onStats }) {
  const { t } = useTranslation();
  const p = scenario.params || {};
  const hasVal = (v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  // 기본 펼침/"변경" 배지 = 이 시나리오가 실제 변경하는 파라미터.
  //   ① 시나리오별 primary_param_keys(=master_data_changes) — 권위 있는 신호(값 없어도 펼침).
  //   ② 현재 params 가 카탈로그 default 와 다른 키 — 사용자 편집 반영(시드된 default 는 제외).
  const defaults = {};
  (scenario.param_defs || []).forEach(d => {
    if (d.default !== undefined && d.default !== null) defaults[d.param_key] = d.default;
  });
  // 조정 판정은 값 기준 — primary_param_keys(시나리오가 손대는 항목)라도 값이 비면 조정이 아니다.
  //   되돌려 비운 파라미터가 계속 강조로 남지 않게 하는 게 목적(IP/RP/MP 와 같은 규칙).
  const changedKeys = new Set((scenario.primary_param_keys || []).filter(k => hasVal(p[k])));
  // scenario.overrides 는 보지 않는다 — 표준/커스텀 시나리오를 고르면 buildDpParams 가 그 값을
  //   이미 params 로 병합하므로(아래 "우선순위" 주석 참조) 판정 소스가 겹친다. 겹친 채로 두면
  //   되돌리기로 params 를 비워도 overrides 쪽 값이 남아 조정 카운트가 안 빠진다.
  Object.entries(p).forEach(([k, v]) => {
    if (hasVal(v) && String(v) !== String(defaults[k] ?? "")) changedKeys.add(k);
  });
  // ── 훅(early return 보다 앞) ──────────────────────────────────────────────
  // **단일 펼침** — 편집 중인 파라미터 하나만. 조정 여부는 접힌 행의 "현행 A → B" 가 알린다.
  const [openKey, setOpenKey] = useState(null);
  // 조정만 보기에서 "파라미터 추가"로 끌어온 행 — 값이 없어 조정 판정에 안 걸리므로 고정한다.
  const [pinnedKeys, setPinnedKeys] = useState(() => new Set());
  useEffect(() => { setOpenKey(null); setPinnedKeys(new Set()); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenario.role, scenario.scenario_cd]);
  const hrchyNames = useDpHrchyNames();
  // 편집을 blur 로 끝낸 직후의 클릭은 무시 — 안 그러면 같은 행을 눌러 끝냈는데 도로 열린다.
  const editGuard = useEditEndGuard();
  const endEdit = () => { editGuard.markEnd(); setOpenKey(null); };
  const toggle = (k) => {
    if (editGuard.justEnded()) return;
    setOpenKey(cur => (cur === k ? null : k));
  };

  // 조정 건수 보고 — 카드 헤더 뷰 토글 라벨.
  const _changedN = (scenario.param_defs || []).filter(d => changedKeys.has(d.param_key)).length;
  const _totalN   = scenario.param_defs?.length || 0;
  useEffect(() => {
    if (scenario.role === "base") return;
    onStats?.({ changed: _changedN, total: _totalN });
    // onStats 는 부모가 매 렌더 새로 만드는 함수라 deps 에서 제외(넣으면 무한 루프).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_changedN, _totalN, scenario.role]);
  const isVisible = (k) => view === "all" || changedKeys.has(k) || pinnedKeys.has(k);
  const pinAndOpen = (k) => {
    setPinnedKeys(s => { const n = new Set(s); n.add(k); return n; });
    setOpenKey(k);
  };

  const inputSx = {
    width: "100%", p: "4px 8px", border: `0.5px solid ${accent}60`,
    borderRadius: 1, fontSize: FONT.BODY, fontFamily: "inherit", color: "#111827",
    bgcolor: "#FFFFFF", outline: "none", boxSizing: "border-box",
    "&:focus": { borderColor: accent },
    "&:disabled": { bgcolor: "#F1F4F8", color: "#9CA3AF", cursor: "not-allowed" },
  };

  // base — 무변경 기준선
  if (scenario.role === "base") {
    return (
      <Box sx={{ p: "10px", borderRadius: 1, bgcolor: "#F1F4F8", border: "0.5px dashed #E5E7EB" }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: "#4B5563" }}>
          {t("dpBaseNoChangeLabel")}
        </Typography>
      </Box>
    );
  }

  // 편집 파라미터 미정의 시나리오(예: 001 — param_catalog/master_data_changes 미적재) graceful 처리
  if (!scenario.param_defs?.length) {
    return (
      <Box sx={{ p: "10px", borderRadius: 1, bgcolor: "#F8F9FB", border: "0.5px dashed #E5E7EB" }}>
        <Typography sx={{ fontSize: FONT.CAPTION, color: "#6B7280" }}>
          {scenario.desc || t("dpNoAdjustableParamsFallback")}
        </Typography>
      </Box>
    );
  }

  // 파라미터 입력 1개
  const renderInput = (def, value) => (
    def.type === "custHrchy" ? (
      // 거래처 계층 — 기존 셀렉트와 동일 크기의 컴팩트 멀티셀렉트(백엔드 API만 재사용).
      <DpCustHrchySelect
        value={value} multi={!!def.multi} disabled={disabled} accent={accent} inputSx={inputSx}
        onChange={(codes) => onParamChange(def.param_key, codes)} />
    ) : def.type === "select" ? (
      <Box component="select" value={value} disabled={disabled}
        onChange={e => onParamChange(def.param_key, e.target.value)}
        sx={{ ...inputSx, ...PARAM_SELECT_W, cursor: disabled ? "not-allowed" : "pointer" }}>
        {(def.options || []).map(o => <option key={o.value} value={o.value}>{t(o.label_key || o.label)}</option>)}
      </Box>
    ) : def.type === "number" ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
        <Box component="input" type="number" value={value} disabled={disabled}
          step={def.step ?? "any"} min={def.min} max={def.max} placeholder="0"
          onChange={e => onParamChange(def.param_key, e.target.value)}
          sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
        {def.unit && (
          <Typography sx={{ fontSize: FONT.BODY, color: "#9CA3AF", flex: "0 0 auto" }}>{def.unit_key ? t(def.unit_key) : def.unit}</Typography>
        )}
      </Box>
    ) : (
      <Box component="input" value={value} disabled={disabled}
        onChange={e => onParamChange(def.param_key, e.target.value)} sx={inputSx} />
    )
  );

  // 아코디언: 단일 펼침 + 접힌 행에 "현행 A → B" (IP/RP 와 동일 패턴)
  const shownDefs = scenario.param_defs.filter(d => isVisible(d.param_key));
  const addCands  = scenario.param_defs.filter(d => !isVisible(d.param_key))
    .map(d => ({ key: d.param_key, label: t(d.label_key || d.label) }));
  return (
    <Stack spacing={0}>
      {shownDefs.map((def, i) => {
        const value   = p[def.param_key] ?? def.default ?? "";
        const changed = changedKeys.has(def.param_key);
        const open    = openKey === def.param_key;
        const last    = i === shownDefs.length - 1;
        // 거래처 계층은 코드(HR-001)가 아니라 이름으로 읽혀야 한다 — 이름을 못 받았으면 코드 폴백.
        const afterText = !changed ? null
          : def.type === "custHrchy"
            ? (Array.isArray(value) ? value : (value ? [value] : []))
                .map(c => hrchyNames?.[c] || c).join(", ") || null
            : fmtParamValue(def, value, t);
        // 단일 컨트롤 타입은 행 클릭 = 값 셀에서 바로 편집. 거래처 계층 트리(custHrchy)만 펼침 유지.
        const inline = isInlineEditable(def);
        const inlineEditing = inline && open && !disabled;
        return (
          <Box key={def.param_key}
            sx={paramRowSx({ accent, applied: changed, last })}>
            <Box onClick={() => toggle(def.param_key)}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, px: PARAM_ROW_PAD_X, py: "6px",
                    height: PARAM_ROW_MIN_H, boxSizing: "border-box", overflow: "hidden",
                    cursor: "pointer", userSelect: "none", "&:hover": { bgcolor: `${accent}08` } }}>
              {/* 라벨은 폭을 고정한다 — grow 를 주면 편집 중 값 셀과 남는 공간을 나눠 가져 입력이 밀린다. */}
              <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: "#111827",
                                       flex: "0 0 36%", minWidth: 0 }}>
                {t(def.label_key || def.label)}
              </Typography>
              <ParamValueCells applied={afterText}
                current={def.current_display} currentPrefix={currentPrefixFor(def, changed, t)}
                accent={accent}
                input={inlineEditing ? renderInput(def, value) : null}
                onEditEnd={endEdit} />
              {/* 값이 있으면 "되돌리기", 값 없이 추가만 한 행이면 "목록에서 빼기". */}
              {!disabled && (
                <ParamRevertButton accent={accent} hidden={!changed && !pinnedKeys.has(def.param_key)}
                  title={t(changed ? "revertToKeepCurrent" : "removeParamLabel")}
                  onClick={() => {
                    // 값을 비운다 — 빈 값은 엔진에 전송되지 않아 그대로 현행이 유지된다.
                    //   카탈로그 기본값(0.96·1.2 …)을 도로 넣으면 "그 값으로 조정한 것"이 되어 버린다.
                    onParamChange(def.param_key, "");
                    // 추가(pin)해서 끌어온 행이면 고정도 함께 푼다(값만 비고 행이 남지 않도록).
                    setPinnedKeys(s => { const n = new Set(s); n.delete(def.param_key); return n; });
                    setOpenKey(cur => (cur === def.param_key ? null : cur));
                  }} />
              )}
              {/* 펼침 표시는 본문이 따로 있는 행에만. 인라인 행에서도 자리는 남긴다(열 정렬 유지). */}
              <Box component="span" sx={{ flex: "0 0 auto", color: "#9CA3AF", fontSize: 11, lineHeight: 1,
                    width: 11, textAlign: "center",
                    ...(inline ? { visibility: "hidden" } : {}),
                    transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
            </Box>
            {open && !inline && (
              <Box sx={{ px: PARAM_ROW_PAD_X, pb: "8px", pt: "4px", borderTop: "0.5px dashed #E5E7EB" }}>
                {renderInput(def, value)}
                {def.current_display && (
                  <Typography sx={{ fontSize: FONT.CAPTION, color: "#9CA3AF", mt: 0.3 }}>
                    {t("dpCurrentValuePrefix", { value: def.current_display })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}
      {view === "changed" && !disabled && (
        <Box sx={{ mt: 0.6 }}>
          <ScenarioParamAddPicker items={addCands} accent={accent} onPick={pinAndOpen} />
        </Box>
      )}
    </Stack>
  );
}

// DP 시나리오 파라미터 렌더러 — **표준 시나리오(카탈로그 param_defs) 전용** (2026-07-15).
// 레거시 4필드(초기화 방식·MF_QTY 공식·계획 기간·계획 주기) 에디터(DpManualScenarioFields)는
// 삭제됨 — 그 항목들은 표준 시나리오 파라미터가 아니라 "버전 생성" 설정(DpVersionModal)이다.
// param_defs 없는 카드(구 캐시 등)는 DpParamDefsFields 가 graceful 안내 박스로 처리한다.
function DpCustomScenarioFields(props) {
  return <DpParamDefsFields {...props} />;
}
DP_MODULE.CustomScenarioFields = DpCustomScenarioFields;

// ── 실현 가능성 경고 (비교 탭) — 백엔드 결정형 판정(scn.feasibility_warnings)을 공용 위젯이 렌더.
//   DP 는 R-DP-01(거래처 제외 = 매출 포기) 하나. 규칙이 늘어도 이 배선은 그대로다.
DP_MODULE.feasibilityCfg = { sectionTitleKey: "feasWarnSectionTitle" };
DP_MODULE.scenarioWarnCount = feasWarnCount;   // KPI 비교표 '경고' 행(공용 슬롯, tone 만 전달)
DP_MODULE.RecHeroBadges = FeasibilityHeroBadges;
// 카탈로그 표준 시나리오(param_defs)도 DpCustomScenarioFields(평면 렌더)가 처리 →
// 공용 ScenarioParamDefsFields(거점/품목 override 모델) 우회.
DP_MODULE.handlesParamDefs = true;
// ── 공용 AiScenarioRecCards 카드 본문 위임 (DP) ──────────────────────────────
// DP는 overrides 매트릭스가 아니라 params(초기수요/계산식/계획범위) 설정 요약을 표시.
DP_MODULE.renderPreviewBody = (sc, { t }) => {
  const p = sc.params || {};
  const keys = ["initial_demand", "compute_measure_formula", "planning_range"];
  const rows = keys
    .filter(k => p[k] != null)
    .map(k => ({ label: t(DP_MODULE.paramLabels[k] || k), value: DP_MODULE.formatParamValue(k, p[k]) }));
  if (!rows.length) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", gap: 0.6, alignItems: "baseline" }}>
          <Typography sx={{ fontSize: FONT.BODY, color: C.neutral, fontWeight: 600, flexShrink: 0, minWidth: 64 }}>{r.label}</Typography>
          <Typography sx={{ fontSize: FONT.BODY, color: "#111827", fontWeight: 600, wordBreak: "keep-all" }}>{r.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};
// makeBlankParams(레거시 4필드 시드) 제거 — 직접 입력 카드는 공통 addBlank 이
// 카탈로그 param_defs 합집합(paramDefsUnion)을 주입한다.

// 산업별 표준 시나리오 (시나리오 구성 탭 — 표준 시나리오 모드). 공통 화면이 mod.templates 로 참조.
DP_MODULE.templates = [
  {
    id: "dp_consumer_bf",
    industry: "dpTplIndustryConsumer",
    name: "dpTplConsumerBfName",
    useCase: "dpTplConsumerBfUseCase",
    desc: "dpTplConsumerBfDesc",
    tags: ["dpTplIndustryConsumer", "dpTplTagBfBased", "standardTagLabel"],
    scenarios: [
      { role: "base", name: "scnDpTplConsumerBfBaseName", desc: "scnDpTplConsumerBfBaseDesc",
        params: { initial_demand: "baselineForecast", compute_measure_formula: [{ MF_QTY: "BF_QTY" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt1", name: "scnDpTplConsumerBfAlt1Name", desc: "scnDpTplConsumerBfAlt1Desc",
        params: { initial_demand: "baselineForecast", compute_measure_formula: [{ MF_QTY: "BF_QTY * 0.95" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt2", name: "scnDpTplConsumerBfAlt2Name", desc: "scnDpTplConsumerBfAlt2Desc",
        params: { initial_demand: "baselineForecast", compute_measure_formula: [{ MF_QTY: "BF_QTY * 1.08" }], planning_range: 18, bucket: "MONTH" } },
    ],
  },
  {
    id: "dp_electronics_sa",
    industry: "dpTplIndustryElectronics",
    name: "dpTplElectronicsSaName",
    useCase: "dpTplElectronicsSaUseCase",
    desc: "dpTplElectronicsSaDesc",
    tags: ["dpTplIndustryElectronics", "dpTplTagSalesBased", "dpTplTagConservative"],
    scenarios: [
      { role: "base", name: "scnDpTplElectronicsSaBaseName", desc: "scnDpTplElectronicsSaBaseDesc",
        params: { initial_demand: "lastSalesActual", compute_measure_formula: [], planning_range: 18, bucket: "MONTH" } },
      { role: "alt1", name: "scnDpTplElectronicsSaAlt1Name", desc: "scnDpTplElectronicsSaAlt1Desc",
        params: { initial_demand: "lastSalesActual", compute_measure_formula: [{ MF_QTY: "MF_QTY * 0.9" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt2", name: "scnDpTplElectronicsSaAlt2Name", desc: "scnDpTplElectronicsSaAlt2Desc",
        params: { initial_demand: "lastSalesActual", compute_measure_formula: [{ MF_QTY: "MF_QTY * 1.15" }], planning_range: 18, bucket: "MONTH" } },
    ],
  },
  {
    id: "dp_seasonal",
    industry: "dpTplIndustrySeasonal",
    name: "dpTplSeasonalName",
    useCase: "dpTplSeasonalUseCase",
    desc: "dpTplSeasonalDesc",
    tags: ["dpTplTagSeasonality", "YoY", "dpTplTagPeakSeason"],
    scenarios: [
      { role: "base", name: "scnDpTplSeasonalBaseName", desc: "scnDpTplSeasonalBaseDesc",
        params: { initial_demand: "salesActualYoY", compute_measure_formula: [], planning_range: 18, bucket: "MONTH" } },
      { role: "alt1", name: "scnDpTplSeasonalAlt1Name", desc: "scnDpTplSeasonalAlt1Desc",
        params: { initial_demand: "salesActualYoY", compute_measure_formula: [{ MF_QTY: "MF_QTY * 1.2" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt2", name: "scnDpTplSeasonalAlt2Name", desc: "scnDpTplSeasonalAlt2Desc",
        params: { initial_demand: "salesActualYoY", compute_measure_formula: [{ MF_QTY: "MF_QTY * 0.95" }], planning_range: 18, bucket: "MONTH" } },
    ],
  },
  {
    id: "dp_newproduct",
    industry: "npi",
    name: "dpTplNewProductName",
    useCase: "dpTplNewProductUseCase",
    desc: "dpTplNewProductDesc",
    tags: ["npi", "dpTplTagFinancePlanBased", "dpTplTagLaunch"],
    scenarios: [
      { role: "base", name: "scnDpTplNewProductBaseName", desc: "scnDpTplNewProductBaseDesc",
        params: { initial_demand: "financePlan", compute_measure_formula: [], planning_range: 18, bucket: "MONTH" } },
      { role: "alt1", name: "scnDpTplNewProductAlt1Name", desc: "scnDpTplNewProductAlt1Desc",
        params: { initial_demand: "financePlan", compute_measure_formula: [{ MF_QTY: "MF_QTY * 0.8" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt2", name: "scnDpTplNewProductAlt2Name", desc: "scnDpTplNewProductAlt2Desc",
        params: { initial_demand: "financePlan", compute_measure_formula: [{ MF_QTY: "MF_QTY * 1.2" }], planning_range: 18, bucket: "MONTH" } },
    ],
  },
  {
    id: "dp_rolling",
    industry: "dpTplIndustryGeneral",
    name: "dpTplRollingName",
    useCase: "dpTplRollingUseCase",
    desc: "dpTplRollingDesc",
    tags: ["dpTplTagRolling", "dpTplTagRecentRelease", "dpTplTagPeriodicUpdate"],
    scenarios: [
      { role: "base", name: "scnDpTplRollingBaseName", desc: "scnDpTplRollingBaseDesc",
        params: { initial_demand: "lastReleasedVersion", compute_measure_formula: [], planning_range: 18, bucket: "MONTH" } },
      { role: "alt1", name: "scnDpTplRollingAlt1Name", desc: "scnDpTplRollingAlt1Desc",
        params: { initial_demand: "lastReleasedVersion", compute_measure_formula: [{ MF_QTY: "MF_QTY * 0.95" }], planning_range: 18, bucket: "MONTH" } },
      { role: "alt2", name: "scnDpTplRollingAlt2Name", desc: "scnDpTplRollingAlt2Desc",
        params: { initial_demand: "lastReleasedVersion", compute_measure_formula: [{ MF_QTY: "MF_QTY * 1.1" }], planning_range: 18, bucket: "MONTH" } },
    ],
  },
];

// 변형 param_defs → 평면 params 시드 (DpParamDefsFields 가 scenario.params 를 읽음).
// 우선순위(낮음→높음): ① param_catalog default → ② DB param_overrides(variant.overrides[].params)
//   → ③ 기존 변형 params → ④ 시나리오/변형 이름의 "×0.96" 배율(비율 파라미터에 반영).
function seedDpVariantParams(variant, fallbackName) {
  const defs = variant.param_defs || [];
  const params = {};
  defs.forEach(d => { if (d.default !== undefined && d.default !== null) params[d.param_key] = d.default; });
  (variant.overrides || []).forEach(ov => { if (ov?.params) Object.assign(params, ov.params); });
  Object.assign(params, variant.params || {});
  if (variant.role !== "base") {
    const m = String(variant.name || fallbackName || "").match(/[×x]\s*([0-9]+(?:\.[0-9]+)?)/i);
    if (m) {
      const ratio = parseFloat(m[1]);
      const numDef = defs.find(d => /ratio/i.test(d.param_key)) || defs.find(d => d.type === "number");
      if (numDef) params[numDef.param_key] = ratio;
    }
  }
  return params;
}

// 표준 시나리오 동적 로딩 (z_ai_scenario 카탈로그) — 다른 모듈(RP/IP/MP)과 동일하게 DB에서 로드.
// DP 는 평면 파라미터(DpParamDefsFields) 모델이므로 override/scope/problem_targets 대신
// 변형 param_defs + 시드된 params 를 사용한다. (param_catalog.DP + per-scenario param_overrides 전제)
DP_MODULE.fetchTemplates = async () => {
  const res = await scenarioService.getScenarios("DP");
  rememberModuleCatalog(DP_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  return (res?.scenarios || []).map(scn => ({
    id:          scn.scenario_cd,
    industry:    scn.source_type === "USER" ? "userTagLabel"
                 : (scn.recommended_flg === "Y" ? "recommendedTagLabel" : "standardTagLabel"),
    // 표시용 i18n 키(*_key)를 우선 사용, 없으면 원문 폴백. LLM 은 원문을 그대로 소비.
    name:        scn.scenario_name_key || scn.scenario_name,
    direction:   scn.scenario_direction_key || scn.scenario_direction,
    useCase:     scn.desc_txt_key || scn.desc_txt,
    desc:        scn.desc_txt_key || scn.desc_txt,
    recommended: scn.recommended_flg === "Y",
    scenarios:   (scn.variants || []).map(v => ({
      ...v,
      scenario_cd: scn.scenario_cd,
      // 이 시나리오가 실제 변경하는 파라미터 키(기본 펼침 힌트). module_params 는 전체 표시용.
      primary_param_keys: scn.primary_param_keys || [],
      params: seedDpVariantParams(v, scn.scenario_name),
    })),
  }));
};

// ── DP 시나리오 플래닝 페이지 ──────────────────────────────────────────────
// AiScenarioPlanning 을 lazy import 하여 순환 참조 방지
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiDpScenarioPlanning({ viewName }) {
  return (
    <React.Suspense fallback={null}>
      <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="DP" />
    </React.Suspense>
  );
}

